<!-- CourseCreationPage.vue -->
<template>
	<div
		class="mcf-container"
		data-component="module-creation-form"
		@mousedown.capture="handlePreviewIntentCapture"
		@mousemove="handleGlobalMouseMove"
		@mouseup="handleGlobalMouseUp"
	>
		<!-- Ghost элемент для drag -->
		<div
			v-if="isDragging && draggedModule"
			class="mcf-drag-ghost"
			:style="ghostStyle"
		>
			<div class="mcf-drag-ghost__card">
				<div
					class="mcf-drag-ghost__cover"
					:style="{
						backgroundImage: draggedModule.coverImage
							? `url(${draggedModule.coverImage})`
							: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
					}"
				></div>
				<div class="mcf-drag-ghost__content">
					<div class="mcf-drag-ghost__title">{{ draggedModule.title }}</div>
				</div>
			</div>
		</div>

		<!-- 1. Секция с опубликованными модулями (показывается ВЫШЕ формы) -->
		<section
			v-if="!showCreationForm"
			class="mcf-modules-section mcf-modules-section--top"
		>
			<div class="mcf-modules-header">
				<h1 class="mcf-modules-title">Модули курса</h1>
				<button
					type="button"
					class="mcf-btn mcf-btn--create"
					@click="handleShowCreationForm"
				>
					Создать модуль
				</button>
			</div>

			<!-- Empty State: когда модулей нет -->
			<div
				v-if="!hasPublishedModules && !hasDraftModules"
				class="mcf-empty-state"
			>
				<div class="mcf-empty-state__illustration">
					<svg class="mcf-empty-state__icon" viewBox="0 0 120 120" fill="none">
						<!-- Коробка -->
						<path
							d="M60 20L100 40V80L60 100L20 80V40L60 20Z"
							fill="#E8F4FD"
							stroke="#0095EC"
							stroke-width="2"
						/>
						<path d="M60 60L100 40" stroke="#0095EC" stroke-width="2" />
						<path d="M60 60L20 40" stroke="#0095EC" stroke-width="2" />
						<path d="M60 60V100" stroke="#0095EC" stroke-width="2" />
						<!-- Плюс -->
						<circle cx="90" cy="30" r="15" fill="#0095EC" />
						<path
							d="M90 24V36M84 30H96"
							stroke="white"
							stroke-width="2.5"
							stroke-linecap="round"
						/>
						<!-- Декоративные элементы -->
						<circle cx="25" cy="25" r="3" fill="#0095EC" opacity="0.3" />
						<circle cx="95" cy="85" r="4" fill="#0095EC" opacity="0.2" />
						<circle cx="15" cy="70" r="2" fill="#0095EC" opacity="0.4" />
					</svg>
				</div>
				<div class="mcf-empty-state__content">
					<h3 class="mcf-empty-state__title">Модули ещё не созданы</h3>
					<p class="mcf-empty-state__text">
						Создайте первый модуль, чтобы структурировать ваш курс. В модуль
						можно добавить уроки, тесты, практические задания и домашние работы.
					</p>
				</div>
			</div>

			<!-- Published Modules -->
			<div v-if="hasPublishedModules" class="mcf-modules-published">
				<h2 class="mcf-section-title">Опубликованные модули</h2>
				<MaterialsSlider
					:items="publishedModules"
					:items-per-page="9"
					:columns="3"
				>
					<template #card="{ item, index }">
						<div
							:ref="el => setCardRef(item.id, el)"
							class="mcf-draggable-card"
							:class="{
								'mcf-draggable-card--dragging':
									isDragging && draggedModule?.id === item.id,
								'mcf-draggable-card--drag-over':
									dragOverModuleId === item.id && draggedModule?.id !== item.id,
								'mcf-draggable-card--placeholder':
									isDragging && draggedModule?.id === item.id,
							}"
							@mousedown.prevent="startDrag(item, $event)"
							@mouseenter="handleCardMouseEnter(item)"
							@mouseleave="handleCardMouseLeave"
						>
							<!-- Drag Handle -->
							<div class="mcf-drag-handle">
								<span class="mcf-drag-handle__dots"></span>
							</div>

							<ModuleCard
								:key="item.id"
								:module-id="item.id"
								:title="item.title"
								:subtitle="item.subtitle"
								:cover-image="item.coverImage"
								:publish-info="item.publishInfo"
								:is-draft="false"
								:is-hidden="item.isHidden ?? item.is_hidden ?? false"
								:is-dragging="isDragging && draggedModule?.id === item.id"
								:show-drag-handle="false"
								@edit="handleEditModule"
								@preview="handlePreviewModule"
								@toggle-visibility="handleToggleModuleVisibility"
								@delete="handleDeleteModule"
							/>
						</div>
					</template>
				</MaterialsSlider>
			</div>

			<!-- Draft Modules (показывается только если есть черновики) -->
			<div v-if="hasDraftModules" class="mcf-modules-drafts">
				<div class="mcf-drafts-header">
					<h2 class="mcf-section-title">Черновики модулей</h2>
					<div class="mcf-drafts-divider"></div>
				</div>
				<MaterialsSlider :items="draftModules" :items-per-page="9" :columns="3">
					<template #card="{ item }">
						<ModuleCard
							:key="item.id"
							:module-id="item.id"
							:title="item.title"
							:subtitle="item.subtitle"
							:cover-image="item.coverImage"
							:publish-info="item.publishInfo"
							:is-draft="true"
							:is-hidden="false"
							@edit="handleEditModule"
							@preview="handlePreviewModule"
							@toggle-visibility="handleToggleModuleVisibility"
							@delete="handleDeleteModule"
						/>
					</template>
				</MaterialsSlider>
			</div>
		</section>

		<!-- 2. Module Creation/Edit Form -->
		<div v-if="showCreationForm" id="module-edit-form" class="mcf-form-wrapper">
			<!-- Page Title -->
			<h1 class="mcf-title">
				{{ isEditingExistingModule ? 'Режим редактирования модуля' : 'Создание модуля' }}
			</h1>
			<p v-if="!isEditingExistingModule" class="mcf-title-hint">
				Сначала задайте название, выберите режим публикации и при желании добавьте фоновое
				изображение. После создания модуля появятся вкладки для наполнения.
			</p>

			<!-- Action Buttons -->
			<div class="mcf-actions">
				<!-- <button
					class="mcf-btn mcf-btn--draft"
					type="button"
					@click="handleSaveAsDraft"
				>
					Сохранить модуль как черновик
				</button> -->
				<button
					v-if="canPreviewCurrentModule"
					class="mcf-btn mcf-btn--preview"
					type="button"
					@mousedown="handlePreviewOpening"
					@click="handlePreviewCurrentModule"
				>
					Предпросмотр модуля
				</button>
				<button
					class="mcf-btn mcf-btn--cancel"
					type="button"
					@click="handleCancel"
				>
					Отменить
				</button>
				<button
					class="mcf-btn mcf-btn--save"
					type="button"
					:disabled="!canSaveModule || saving || isModuleContentLoading"
					:title="saveButtonTooltip"
					@click="handleSaveModule"
				>
					{{ saveButtonText }}
				</button>
			</div>

			<!-- Validation Warning -->
			<div
				v-if="!canSaveModule && showValidationWarning"
				class="mcf-validation-warning"
			>
				<div class="mcf-validation-warning__icon">⚠️</div>
				<div class="mcf-validation-warning__content">
					<h3 class="mcf-validation-warning__title">
						Модуль не готов к публикации
					</h3>
					<p class="mcf-validation-warning__text">
						Для публикации модуля необходимо создать минимум по одному элементу
						каждого типа:
					</p>
					<ul class="mcf-validation-warning__list">
						<li v-if="!hasLessons" class="mcf-validation-warning__item">
							❌ Уроки (минимум 1)
						</li>
						<li v-if="!hasTests" class="mcf-validation-warning__item">
							❌ Тесты (минимум 1)
						</li>
						<li v-if="!hasHomework" class="mcf-validation-warning__item">
							❌ Домашние работы (минимум 1)
						</li>
						<li v-if="!hasPractice" class="mcf-validation-warning__item">
							❌ Практические задания (минимум 1)
						</li>
					</ul>
					<p class="mcf-validation-warning__hint">
						💡 Вы можете сохранить модуль как черновик и продолжить работу позже
					</p>
				</div>
			</div>

			<!-- Module Cover Section -->
			<section class="mcf-cover">
				<div class="mcf-cover__bg-overlay"></div>
				<div
					class="mcf-cover__bg"
					:style="
						coverImagePreview
							? {
									backgroundImage: `url(${coverImagePreview})`,
									backgroundSize: 'cover',
									backgroundPosition: 'center',
									backgroundRepeat: 'no-repeat',
							  }
							: {}
					"
				></div>

				<!-- Upload/Change Cover Button -->
				<button class="mcf-upload-btn" type="button" @click="handleUploadCover">
					<div class="mcf-upload-btn__bg"></div>
					<span class="mcf-upload-btn__text">
						{{ coverImagePreview ? 'Изменить обложку' : 'Загрузить обложку' }}
					</span>
				</button>

				<!-- Remove Cover Button (показывается только если есть обложка) -->
				<button
					v-if="coverImagePreview"
					class="mcf-remove-cover-btn"
					type="button"
					@click="handleRemoveCover"
					title="Удалить обложку"
				>
					<div class="mcf-remove-cover-btn__bg"></div>
					<span class="mcf-remove-cover-btn__icon">✕</span>
				</button>

				<!-- Module Title -->
				<div class="mcf-title-editor">
					<div class="mcf-title-editor__bg"></div>
					<input
						v-model="moduleTitle"
						class="mcf-title-editor__input"
						type="text"
						placeholder="Название модуля"
						@input="handleTitleChange"
					/>
				</div>
			</section>

			<!-- Auto-publish Settings -->
			<aside class="mcf-settings">
				<!-- Иконка заголовка -->
				<div class="mcf-settings__header">
					<div class="mcf-settings__icon">
						<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
							<path
								d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"
								fill="currentColor"
							/>
							<path
								d="M12.5 7H11v6l5.25 3.15.75-1.23-4.5-2.67z"
								fill="currentColor"
							/>
						</svg>
					</div>
					<h2 class="mcf-settings__title">Настройки автопубликации</h2>
				</div>

				<!-- Publish Options -->
				<div class="mcf-radio-group">
					<!-- Immediately Option -->
					<label class="mcf-radio">
						<input
							v-model="publishType"
							class="mcf-radio__input"
							type="radio"
							name="publish-type"
							value="immediate"
							@change="handlePublishTypeChange"
						/>
						<span class="mcf-radio__custom"></span>
						<div class="mcf-radio__content">
							<div class="mcf-radio__title-wrapper">
								<span class="mcf-radio__emoji">⚡</span>
								<span class="mcf-radio__title">Сразу</span>
							</div>
							<span class="mcf-radio__desc">
								Модуль откроется студентам сразу после создания
							</span>
						</div>
					</label>

					<!-- Scheduled Option -->
					<label class="mcf-radio">
						<input
							v-model="publishType"
							class="mcf-radio__input"
							type="radio"
							name="publish-type"
							value="scheduled"
							@change="handlePublishTypeChange"
						/>
						<span class="mcf-radio__custom"></span>
						<div class="mcf-radio__content">
							<div class="mcf-radio__title-wrapper">
								<span class="mcf-radio__emoji">📅</span>
								<span class="mcf-radio__title">По расписанию</span>
							</div>
							<span class="mcf-radio__desc">
								Задайте дату и время для публикации
							</span>
						</div>
					</label>
				</div>

				<!-- Date & Time Inputs -->
				<div class="mcf-datetime-group">
					<!-- Date Input -->
					<div class="mcf-field mcf-field--date">
						<label class="mcf-field__label">
							Дата публикации
						</label>
						<DateTimePicker
							v-model="publishDate"
							mode="date"
							:disabled="publishType === 'immediate'"
							@change="handleDateChange"
						/>
					</div>

					<!-- Time Input -->
					<div class="mcf-field mcf-field--time">
						<label class="mcf-field__label">
							Время публикации
						</label>
						<DateTimePicker
							v-model="publishTime"
							mode="time"
							:disabled="publishType === 'immediate'"
							@change="handleTimeChange"
						/>
					</div>
				</div>

				<!-- Info Badge -->
				<div v-if="publishType === 'scheduled'" class="mcf-settings__info">
					<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
						<path
							d="M8 1.5C4.41 1.5 1.5 4.41 1.5 8s2.91 6.5 6.5 6.5 6.5-2.91 6.5-6.5S11.59 1.5 8 1.5zm.5 9.75h-1v-1h1v1zm0-2.5h-1v-4h1v4z"
							fill="currentColor"
						/>
					</svg>
					<span>Модуль автоматически опубликуется в указанное время</span>
				</div>
			</aside>

			<!-- 4. Module Elements Section (показывается ТОЛЬКО когда форма открыта) -->
			<div v-if="isModuleContentLoading" class="mcf-module-loading">
				Загружаем материалы модуля...
			</div>
			<ModuleTestsSection
				:module-id="editingModule?.id"
				:course-id="courseId"
				:is-local-mode="true"
				:show-settings-tab="true"
				:hide-tabs="isCreatingNewModule"
				:initial-materials="moduleElements"
				@element-created="handleElementCreated"
				@element-updated="handleElementUpdated"
				@element-deleted="handleElementDeleted"
				@material-visibility-changed="handleMaterialVisibilityChanged"
				@preview-opening="handlePreviewOpening"
				@preview-closed="handlePreviewClosed"
			>
				<template #settings>
					<section class="mcf-details">
						<div class="mcf-details__grid">
							<!-- Description Field -->
							<div class="mcf-field mcf-field--full">
								<label class="mcf-field__label" for="module-description">
									Описание модуля (необязательно)
								</label>
								<textarea
									id="module-description"
									v-model="moduleDescription"
									class="mcf-field__textarea"
									placeholder="Краткое описание содержания модуля..."
									rows="3"
									maxlength="500"
									@input="handleDescriptionChange"
								></textarea>
								<div
									class="mcf-field__counter"
									:class="{
										'mcf-field__counter--limit':
											(moduleDescription || '').length >= 500,
									}"
								>
									{{ (moduleDescription || '').length }}/500
								</div>
							</div>

							<!-- Duration Field -->
							<div class="mcf-field">
								<label class="mcf-field__label" for="module-duration">
									Продолжительность (часы)
								</label>
								<input
									id="module-duration"
									v-model.number="moduleDuration"
									class="mcf-field__input"
									type="number"
									min="0"
									step="0.01"
									placeholder="Например: 1.45"
									@input="handleDurationChange"
								/>
								<span class="mcf-field__hint"
									>Можно указать дробное значение, например: 1.5 или 2.25</span
								>
							</div>

							<!-- Completion Policy -->
							<div class="mcf-field mcf-field--full">
								<label class="mcf-field__label">Завершение модуля</label>
								<div class="mcf-completion">
									<label class="mcf-checkbox">
										<input
											v-model="completionEnabled"
											type="checkbox"
											class="mcf-checkbox__input"
											@change="markAsModified"
										/>
										<span class="mcf-checkbox__text"
											>Требовать финальный тест для завершения</span
										>
									</label>
									<div class="mcf-completion__row">
										<label class="mcf-field__label" for="final-test-select">
											Финальный тест
										</label>
										<select
											id="final-test-select"
											v-model="completionFinalTestId"
											class="mcf-field__input"
											:disabled="!completionEnabled || !testsAvailable"
											@change="markAsModified"
										>
											<option value="">Выберите тест</option>
											<option
												v-for="test in moduleElements.tests"
												:key="test.id"
												:value="String(test.id)"
											>
												{{ test.title || `Тест ${test.id}` }}
											</option>
										</select>
										<p
											v-if="completionEnabled && !testsAvailable"
											class="mcf-completion__hint mcf-completion__hint--warning"
										>
											Сначала создайте тесты в этом модуле.
										</p>
										<p
											v-if="completionEnabled && testsAvailable && !completionFinalTestId"
											class="mcf-completion__hint mcf-completion__hint--warning"
										>
											Выберите финальный тест, чтобы настроить требования к завершению и окно доступа.
										</p>
									</div>
										<div class="mcf-completion__row mcf-completion__row--rules">
											<label class="mcf-field__label">Требования к завершению для доступа</label>
											<div class="mcf-completion__rules">
												<label class="mcf-checkbox mcf-checkbox--inline">
													<input
														v-model="finalTestRequireLessons"
														type="checkbox"
														class="mcf-checkbox__input"
														:disabled="!completionEnabled || !completionFinalTestId"
														@change="markAsModified"
													/>
													<span class="mcf-checkbox__text">Уроки</span>
												</label>
												<label class="mcf-checkbox mcf-checkbox--inline">
													<input
														v-model="finalTestRequirePractice"
														type="checkbox"
														class="mcf-checkbox__input"
														:disabled="!completionEnabled || !completionFinalTestId"
														@change="markAsModified"
													/>
													<span class="mcf-checkbox__text">Практики</span>
												</label>
												<label class="mcf-checkbox mcf-checkbox--inline">
													<input
														v-model="finalTestRequireHomework"
														type="checkbox"
														class="mcf-checkbox__input"
														:disabled="!completionEnabled || !completionFinalTestId"
														@change="markAsModified"
													/>
													<span class="mcf-checkbox__text">Домашние задания</span>
												</label>
												<label class="mcf-checkbox mcf-checkbox--inline">
													<input
														v-model="finalTestRequireTests"
														type="checkbox"
														class="mcf-checkbox__input"
														:disabled="!completionEnabled || !completionFinalTestId"
														@change="markAsModified"
													/>
													<span class="mcf-checkbox__text">Тесты</span>
												</label>
											</div>
										</div>
										<div class="mcf-completion__row mcf-completion__row--time">
											<label class="mcf-field__label">Окно доступа</label>
											<label class="mcf-checkbox">
												<input
													v-model="finalTestWindowEnabled"
													type="checkbox"
													class="mcf-checkbox__input"
													:disabled="!completionEnabled || !completionFinalTestId"
													@change="markAsModified"
												/>
												<span class="mcf-checkbox__text">Ограничить по времени</span>
											</label>
											<div class="mcf-completion__window">
												<div class="mcf-window-field">
													<label class="mcf-field__label mcf-field__label--compact">Дата доступа</label>
													<DateTimePicker
														v-model="finalTestWindowDate"
														mode="date"
														placeholder="Выберите дату"
														:disabled="!completionEnabled || !completionFinalTestId || !finalTestWindowEnabled"
														@change="markAsModified"
													/>
												</div>
												<div class="mcf-window-field">
													<label class="mcf-field__label mcf-field__label--compact">Время доступа</label>
													<DateTimePicker
														v-model="finalTestWindowTime"
														mode="time"
														placeholder="Выберите время"
														:disabled="!completionEnabled || !completionFinalTestId || !finalTestWindowEnabled"
														@change="markAsModified"
													/>
												</div>
												<div class="mcf-window-field mcf-window-field--duration">
													<label class="mcf-field__label mcf-field__label--compact">
														Количество времени доступа, минут
													</label>
													<input
														v-model.number="finalTestWindowDurationMinutes"
														class="mcf-field__input"
														type="number"
														min="1"
														step="1"
														placeholder="120"
														:disabled="!completionEnabled || !completionFinalTestId || !finalTestWindowEnabled"
														@input="markAsModified"
													/>
												</div>
											</div>
											<p class="mcf-completion__hint">
												Студенты смогут начать тест только внутри указанного окна.
											</p>
										</div>
								</div>
							</div>
						</div>
					</section>
				</template>
			</ModuleTestsSection>
		</div>
		</div>
</template>

<script>
import MaterialsSlider from '@/components/MaterialsSlider.vue'
import ModuleCard from '@/components/ModuleCard.vue'
import DateTimePicker from '@/components/ui/DateTimePicker.vue'
import ModuleTestsSection from './ModuleTestsSection.vue'
import authState from '@/utils/authState'
import { openInternalRouteInContext } from '@/utils/bitrixInternalNavigation'

export default {
	name: 'ModuleCreationForm',

	components: {
		MaterialsSlider,
		ModuleCard,
		DateTimePicker,
		ModuleTestsSection,
	},

	//  NAVIGATION GUARD - Блокирует переходы через Vue Router
	beforeRouteLeave(to, from, next) {
		// ✅ ПРИОРИТЕТ 1: Проверяем глобальный флаг logout
		if (authState.isLoggingOut) {
			console.log(
				'🔓 [beforeRouteLeave] Logout in progress, allowing navigation',
			)
			this.closeEditingSession()
			next()
			return
		}

		// Проверяем, открыта ли форма редактирования
		if (this.showCreationForm) {
			if (this.mustApplyChangesBeforeExit) {
				alert(
					'Сначала нажмите "Применить изменения". Выход из редактирования с несохраненными изменениями заблокирован.'
				)
				next(false)
				return
			}
			// ✅ ПРИОРИТЕТ 2: Проверяем маршрут на logout-related
			if (this.isLogoutRoute(to)) {
				console.log(
					'🔓 [beforeRouteLeave] Logout route detected, allowing navigation',
				)
				this.closeEditingSession()
				next()
				return
			}
			// Формируем сообщение в зависимости от наличия изменений
			let confirmMessage = 'Вы находитесь в режиме редактирования модуля.'

			if (this.hasRealChanges) {
				confirmMessage = 'У вас есть несохраненные изменения в модуле.'
			}

			confirmMessage +=
				'\n\nВы уверены, что хотите покинуть страницу?\nВсе несохраненные изменения будут потеряны.'

			// Показываем диалог подтверждения
			const answer = confirm(confirmMessage)

			if (answer) {
				// Пользователь подтвердил выход
				this.clearEditingStateFromLocalStorage()
				this.resetForm()
				next() // Разрешаем переход
			} else {
				// Пользователь отменил - остаемся на странице
				next(false) // Блокируем переход
			}
		} else {
			// Форма не открыта - свободно разрешаем переход
			next()
		}
	},

	props: {
		// Флаг для автоматического открытия формы создания модуля
		autoOpenForm: {
			type: Boolean,
			default: false,
		},
		// ID курса для связи модулей
		courseId: {
			type: [Number, String],
			default: null,
		},
		// ID редактируемого модуля
		editingModuleId: {
			type: [Number, String],
			default: null,
		},
		// Список модулей (передается из родительского компонента)
		modules: {
			type: Array,
			default: () => [],
		},
		saving: {
			type: Boolean,
			default: false,
		},
	},

	data() {
		return {
			// Форма создания/редактирования
			showCreationForm: false,
			editingModule: null,

			// Данные формы
			moduleTitle: '',
			moduleDescription: '',
			moduleDuration: null,
			completionEnabled: false,
			completionFinalTestId: '',
			completionCapPercent: 95,
			finalTestRequireLessons: false,
			finalTestRequirePractice: false,
			finalTestRequireHomework: false,
			finalTestRequireTests: false,
			finalTestWindowEnabled: false,
			finalTestWindowDate: '',
			finalTestWindowTime: '',
			finalTestWindowDurationMinutes: 120,
			coverImage: null,
			coverImagePreview: null, // URL для превью обложки
			publishType: 'scheduled',
			// ⚠️ HTML input type="date" требует формат YYYY-MM-DD
			publishDate: '2025-11-24',
			publishTime: '23:59',
			isDraft: false,
			isModified: false,

			// Элементы текущего модуля
			moduleElements: {
				lessons: [],
				tests: [],
				homework: [],
				practice: [],
			},

			//  Отслеживание удаленных элементов из БД
			deletedElements: {
				lessons: [],
				tests: [],
				homework: [],
				practice: [],
			},

			// Валидация
			showValidationWarning: false,
			initialModuleSnapshot: null,

			// Drag and Drop state
			isDragging: false,
			draggedModule: null,
			dragOverModuleId: null,
			dragStartPos: { x: 0, y: 0 },
			currentPos: { x: 0, y: 0 },
			cardRefs: {},

			// Интервал для проверки автопубликации
			autoPublishCheckInterval: null,
			autoSyncTimer: null,
			isAutoSyncInProgress: false,
			pendingAutoSyncReason: null,
			isModuleContentLoading: false,
			isRestoringModuleData: false,
			storageQuotaWarningShown: false,
			moduleLoadRequestId: 0,
			modulePrefetchCache: {},
			modulePrefetchInFlight: {},
			modulePrefetchTimer: null,
			modulePrefetchTargetId: null,
			moduleCacheTtlMs: 90 * 1000,
			modulePrefetchDelayMs: 180,
			previewDirtyGuardActive: false,
		}
	},

	watch: {
		//  Отслеживаем изменение courseId для восстановления состояния
		courseId: {
			immediate: true,
			handler(newValue) {
				if (!newValue) return
				this.modulePrefetchCache = {}
				this.modulePrefetchInFlight = {}
				this.clearModulePrefetchTimer()

				console.log(
					'🔍 courseId определен, пытаемся восстановить состояние:',
					newValue,
				)
				this.loadEditingStateFromLocalStorage()
			},
		},
		// Отслеживаем изменение пропса autoOpenForm
		autoOpenForm(newValue) {
			if (newValue === true) {
				this.handleShowCreationForm()
			}
		},
		// Отслеживаем изменение ID редактируемого модуля
		async editingModuleId(newValue) {
			if (newValue) {
				const currentId = this.editingModule?.id
				if (
					currentId !== undefined &&
					currentId !== null &&
					String(currentId) === String(newValue)
				) {
					return
				}

				const wasTempModuleId = String(this.editingModule?.id || '').startsWith(
					'temp_',
				)
				await this.loadModuleForEdit(newValue)
				// Автоскролл: после создания — к вкладкам, иначе — к форме
				this.$nextTick(() => {
					setTimeout(() => {
						const tabsElement = document.querySelector('.mts-tabs')
						const formElement = document.getElementById('module-edit-form')
						if (wasTempModuleId && tabsElement) {
							tabsElement.scrollIntoView({
								behavior: 'smooth',
								block: 'start',
							})
							return
						}
						if (formElement) {
							formElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
						}
					}, 100)
				})
			}
		},
		//  Отслеживание изменений для автосохранения
		moduleTitle() {
			this.saveEditingStateToLocalStorage()
		},
		publishType() {
			this.saveEditingStateToLocalStorage()
		},
		publishDate() {
			this.saveEditingStateToLocalStorage()
		},
		publishTime() {
			this.saveEditingStateToLocalStorage()
		},
		moduleDescription() {
			this.saveEditingStateToLocalStorage()
		},
		moduleDuration() {
			this.saveEditingStateToLocalStorage()
		},
		completionEnabled() {
			if (this.completionEnabled) {
				this.completionCapPercent = 95
			}
			this.saveEditingStateToLocalStorage()
		},
		completionFinalTestId() {
			if (this.completionFinalTestId) {
				this.completionCapPercent = 95
			}
			this.saveEditingStateToLocalStorage()
		},
		moduleElements: {
			handler() {
				this.saveEditingStateToLocalStorage()
			},
			deep: true,
		},
		'moduleElements.tests': {
			handler() {
				if (!this.completionFinalTestId) return
				const exists = this.moduleElements.tests.some(
					test => String(test.id) === String(this.completionFinalTestId),
				)
				if (!exists) {
					this.completionFinalTestId = ''
					this.completionEnabled = false
				}
			},
			deep: true,
		},
		deletedElements: {
			handler() {
				this.saveEditingStateToLocalStorage()
			},
			deep: true,
		},
	},

	computed: {
		// Стиль для ghost элемента
		ghostStyle() {
			if (!this.isDragging) return { display: 'none' }
			return {
				left: `${this.currentPos.x}px`,
				top: `${this.currentPos.y}px`,
				display: 'block',
			}
		},

		// Есть ли опубликованные модули
		hasPublishedModules() {
			return this.publishedModules.length > 0
		},

		// Есть ли черновики модулей
		hasDraftModules() {
			return this.draftModules.length > 0
		},

		// Опубликованные модули
		publishedModules() {
			return this.modules.filter(module => !this.isDraftModule(module))
		},

		// Черновики модулей
		draftModules() {
			return this.modules.filter(module => this.isDraftModule(module))
		},

		// Проверка наличия элементов каждого типа
		isEditingExistingModule() {
			return (
				!!this.editingModule &&
				!String(this.editingModule.id || '').startsWith('temp_')
			)
		},
		canPreviewCurrentModule() {
			return (
				this.showCreationForm &&
				!!this.editingModule?.id &&
				!String(this.editingModule.id).startsWith('temp_')
			)
		},

		isCreatingNewModule() {
			return this.showCreationForm && !this.isEditingExistingModule
		},

		initialPublishState() {
			if (!this.initialModuleSnapshot) return null
			try {
				const parsed = JSON.parse(this.initialModuleSnapshot)
				return {
					publishType: parsed?.publishType || '',
					publishDate: parsed?.publishDate || '',
					publishTime: parsed?.publishTime || '',
				}
			} catch (error) {
				console.warn('Не удалось разобрать initialModuleSnapshot:', error)
				return null
			}
		},

		hasRealChanges() {
			if (!this.showCreationForm) return false
			if (this.isModified) return true
			if (!this.initialModuleSnapshot) return false
			return this.getCurrentSnapshot() !== this.initialModuleSnapshot
		},

		hasLessons() {
			return this.moduleElements.lessons.length > 0
		},

		hasTests() {
			return this.moduleElements.tests.length > 0
		},

		hasHomework() {
			return this.moduleElements.homework.length > 0
		},

		hasPractice() {
			return this.moduleElements.practice.length > 0
		},

		testsAvailable() {
			return (
				Array.isArray(this.moduleElements.tests) &&
				this.moduleElements.tests.length > 0
			)
		},

		// Можно ли сохранить модуль (все элементы созданы)
		canSaveModule() {
			// ВРЕМЕННО ОТКЛЮЧЕНА ВАЛИДАЦИЯ - для разработки
			return this.isFormValid && this.hasRealChanges

			// Раскомментируйте для включения полной валидации:
			// return (
			// 	this.isFormValid &&
			// 	this.hasLessons &&
			// 	this.hasTests &&
			// 	this.hasHomework &&
			// 	this.hasPractice
			// )
		},

		// Текст кнопки сохранения
		saveButtonText() {
			if (this.isEditingExistingModule) {
				return 'Применить изменения'
			}
			return 'Создать модуль'
		},

		// Подсказка для кнопки сохранения
		mustApplyChangesBeforeExit() {
			return this.isEditingExistingModule && this.canSaveModule
		},
		saveButtonTooltip() {
			if (!this.isFormValid) {
				return 'Заполните все обязательные поля'
			}
			if (!this.hasRealChanges) {
				return 'Нет изменений для сохранения'
			}
			if (this.isEditingExistingModule) {
				return 'Применить изменения'
			}
			return 'Создать модуль'
		},

		isFormValid() {
			if (!this.moduleTitle.trim()) return false
			if (this.completionEnabled && !this.completionFinalTestId) return false
			if (this.publishType === 'scheduled') {
				if (this.publishDate && this.publishTime) return true
				const initial = this.initialPublishState
				if (
					this.isEditingExistingModule &&
					initial &&
					initial.publishType === 'scheduled' &&
					!initial.publishDate &&
					!initial.publishTime &&
					!this.publishDate &&
					!this.publishTime
				) {
					return true
				}
				return false
			}
			return true
		},

		publishDateTime() {
			if (this.publishType === 'immediate') return null
			return {
				date: this.publishDate,
				time: this.publishTime,
			}
		},

		//  НОВЫЙ COMPUTED: Отформатированная дата для отображения (DD-MM-YYYY)
		formattedPublishDate() {
			if (!this.publishDate) return ''
			const [year, month, day] = this.publishDate.split('-')
			return `${day}-${month}-${year}`
		},
	},

	methods: {
		safeRevokeBlobUrl(url) {
			if (!url || typeof url !== 'string' || !url.startsWith('blob:')) return
			try {
				URL.revokeObjectURL(url)
			} catch (error) {
				console.warn('Не удалось освободить blob URL:', error)
			}
		},
		deferRevokeBlobUrl(url) {
			if (!url || typeof url !== 'string' || !url.startsWith('blob:')) return
			setTimeout(() => {
				this.safeRevokeBlobUrl(url)
			}, 0)
		},
		markAsModified() {
			this.refreshModificationState()
		},
		refreshModificationState() {
			if (this.previewDirtyGuardActive) {
				this.isModified = false
				this.saveEditingStateToLocalStorage()
				return
			}

			if (!this.showCreationForm || !this.initialModuleSnapshot) {
				this.isModified = false
				this.saveEditingStateToLocalStorage()
				return
			}

			this.isModified = this.getCurrentSnapshot() !== this.initialModuleSnapshot
			this.saveEditingStateToLocalStorage()
		},
		clearAutoSyncState() {
			if (this.autoSyncTimer) {
				clearTimeout(this.autoSyncTimer)
				this.autoSyncTimer = null
			}
			this.pendingAutoSyncReason = null
		},
		buildAutoSyncModuleData() {
			const hasSchedule = this.publishType === 'scheduled'
			const publishDate = hasSchedule ? this.publishDate || null : null
			const publishTime = hasSchedule ? this.publishTime || null : null
			const immediateLabel = '\u0421\u0440\u0430\u0437\u0443'
			const publishDateForInfo = this.formatDateForInfo(this.publishDate)
			const hasValidSchedule =
				hasSchedule && publishDate && publishTime
			let isHidden = false
			if (hasValidSchedule) {
				const publishAt = new Date(`${publishDate}T${publishTime}`)
				isHidden = Number.isNaN(publishAt.getTime())
					? true
					: publishAt.getTime() > Date.now()
			}

			const completionPolicy =
				this.completionEnabled && this.completionFinalTestId
					? {
							final_test_id: Number(this.completionFinalTestId),
							cap_percent: 95,
					  }
					: null
			if (completionPolicy) {
				const { accessRules, error } = this.buildCompletionAccessRules()
				if (!error && accessRules) {
					completionPolicy.access_rules = accessRules
				}
			}

			return {
				id: this.editingModule?.id,
				title: this.moduleTitle,
				subtitle: '',
				description: this.moduleDescription || '',
				duration_hours: this.moduleDuration || null,
				coverImage: this.coverImage,
				publishType: this.publishType,
				publishDate: this.publishDate,
				publishTime: this.publishTime,
				publishInfo:
					this.publishType === 'immediate'
						? immediateLabel
						: `${publishDateForInfo} ${this.publishTime}`,
				isDraft: false,
				isHidden,
				elements: { ...this.moduleElements },
				completion_policy: completionPolicy,
				deletedElements: { ...this.deletedElements },
			}
		},
		scheduleAutoSync(reason = 'material-update') {
			if (!this.isEditingExistingModule) {
				return
			}
			this.pendingAutoSyncReason = reason
			if (this.autoSyncTimer) {
				clearTimeout(this.autoSyncTimer)
			}
			this.autoSyncTimer = setTimeout(() => {
				this.autoSyncTimer = null
				this.flushAutoSync()
			}, 500)
		},
		flushAutoSync() {
			if (!this.isEditingExistingModule) {
				return
			}
			if (this.isAutoSyncInProgress) {
				return
			}

			this.isAutoSyncInProgress = true
			const reason = this.pendingAutoSyncReason || 'material-update'
			this.pendingAutoSyncReason = null

			const finalize = () => {
				this.isAutoSyncInProgress = false
				if (this.pendingAutoSyncReason) {
					const nextReason = this.pendingAutoSyncReason
					this.pendingAutoSyncReason = null
					this.scheduleAutoSync(nextReason)
				}
			}

			const moduleData = this.buildAutoSyncModuleData()
			moduleData.__autoSync = true
			moduleData.__silent = true
			moduleData.__keepEditorOpen = true
			moduleData.__syncReason = reason
			moduleData.__onSaveSuccess = () => {
				finalize()
			}
			moduleData.__onSaveError = error => {
				console.error('❌ Автосинхронизация модуля не выполнена:', error)
				console.warn(
					'Изменения сохранены локально. Для фиксации в БД нажмите "Сохранить модуль".'
				)
				finalize()
			}

			this.$emit('save-module', moduleData)
		},
		normalizeModulePublishDateTime(module) {
			let publishDate = module?.publish_date || ''
			let publishTime = module?.publish_time || ''
			const publishInfoRaw = module?.publish_info || ''
			const rawPublishAt = module?.publish_at || module?.publishAt || ''
			const publishedPrefix =
				'\u041e\u043f\u0443\u0431\u043b\u0438\u043a\u043e\u0432\u0430\u043d\u043e '
			const immediateLabel = '\u0421\u0440\u0430\u0437\u0443'

			const parseDatePart = value => {
				if (!value || typeof value !== 'string') return null
				const trimmed = value.trim()
				if (!trimmed) return null
				if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
					const [year, month, day] = trimmed.split('-').map(Number)
					return { year, month, day }
				}
				if (/^\d{2}\.\d{2}\.\d{4}$/.test(trimmed)) {
					const [day, month, year] = trimmed.split('.').map(Number)
					return { year, month, day }
				}
				if (/^\d{2}-\d{2}-\d{4}$/.test(trimmed)) {
					const [day, month, year] = trimmed.split('-').map(Number)
					return { year, month, day }
				}
				return null
			}
			const parseTimePart = value => {
				if (!value || typeof value !== 'string') return null
				const parts = value.split(':')
				if (parts.length < 2) return null
				const hours = Number(parts[0])
				const minutes = Number(parts[1])
				const seconds = parts.length > 2 ? Number(parts[2]) : 0
				if (
					Number.isNaN(hours) ||
					Number.isNaN(minutes) ||
					Number.isNaN(seconds)
				) {
					return null
				}
				return { hours, minutes, seconds }
			}
			const buildDate = (datePart, timePart) => {
				const date = parseDatePart(datePart)
				if (!date) return null
				const time = parseTimePart(timePart) || {
					hours: 0,
					minutes: 0,
					seconds: 0,
				}
				return new Date(
					date.year,
					date.month - 1,
					date.day,
					time.hours,
					time.minutes,
					time.seconds,
				)
			}
			const parseLocalDateTime = value => {
				if (!value || typeof value !== 'string') return null
				const trimmed = value.trim()
				if (!trimmed) return null
				if (trimmed.includes('T')) {
					const parsed = new Date(trimmed)
					return Number.isNaN(parsed.getTime()) ? null : parsed
				}
				if (trimmed.includes(' ')) {
					const [datePart, timePart] = trimmed.split(' ')
					return buildDate(datePart, timePart)
				}
				return buildDate(trimmed, null)
			}
			const applyDate = (date, options = {}) => {
				const year = date.getFullYear()
				const month = String(date.getMonth() + 1).padStart(2, '0')
				const day = String(date.getDate()).padStart(2, '0')
				const hours = String(date.getHours()).padStart(2, '0')
				const minutes = String(date.getMinutes()).padStart(2, '0')
				const dateValue = `${year}-${month}-${day}`
				const timeValue = `${hours}:${minutes}`
				if (!publishDate || options.forceDate) {
					publishDate = dateValue
				}
				if (!publishTime || options.forceTime) {
					publishTime = timeValue
				}
			}

			if (
				publishDate &&
				(publishDate.includes('T') || publishDate.includes(' '))
			) {
				const parsed = parseLocalDateTime(publishDate)
				if (parsed) {
					const year = parsed.getFullYear()
					const month = String(parsed.getMonth() + 1).padStart(2, '0')
					const day = String(parsed.getDate()).padStart(2, '0')
					const hours = String(parsed.getHours()).padStart(2, '0')
					const minutes = String(parsed.getMinutes()).padStart(2, '0')
					publishDate = `${year}-${month}-${day}`
					if (!publishTime) {
						publishTime = `${hours}:${minutes}`
					}
				}
			}

			const rawParsed = parseLocalDateTime(rawPublishAt)
			if (rawParsed) {
				applyDate(rawParsed)
			}

			if ((!publishDate || !publishTime) && publishInfoRaw) {
				const trimmed = publishInfoRaw.trim()
				const lower = trimmed.toLowerCase()
				const normalized = lower.startsWith(publishedPrefix.toLowerCase())
					? trimmed.slice(publishedPrefix.length).trim()
					: trimmed
				if (normalized && normalized !== immediateLabel) {
					let parsed = null
					if (normalized.includes('T') && normalized.includes(' ')) {
						const infoParts = normalized.split(' ')
						const timeCandidate = infoParts[infoParts.length - 1]
						const timeParts = parseTimePart(timeCandidate)
						const dateCandidate = parseLocalDateTime(infoParts[0])
						if (dateCandidate && timeParts) {
							const year = dateCandidate.getFullYear()
							const month = String(dateCandidate.getMonth() + 1).padStart(
								2,
								'0',
							)
							const day = String(dateCandidate.getDate()).padStart(2, '0')
							const hours = String(timeParts.hours).padStart(2, '0')
							const minutes = String(timeParts.minutes).padStart(2, '0')
							const infoDate = `${year}-${month}-${day}`
							const infoTime = `${hours}:${minutes}`
							if (!publishDate) {
								publishDate = infoDate
							}
							if (!publishTime) {
								publishTime = infoTime
							}
							parsed = buildDate(infoDate, infoTime)
						}
					}
					if (!parsed) {
						parsed = parseLocalDateTime(normalized)
					}
					if (parsed) {
						applyDate(parsed)
					}
				}
			}

			if (publishTime && publishTime.length > 5) {
				publishTime = publishTime.slice(0, 5)
			}

			return { publishDate, publishTime }
		},

		formatDateForInfo(value) {
			if (!value || typeof value !== 'string') return ''
			const trimmed = value.trim()
			if (!trimmed) return ''
			if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
				const [year, month, day] = trimmed.split('-')
				return `${day}-${month}-${year}`
			}
			if (/^\d{2}\.\d{2}\.\d{4}$/.test(trimmed)) {
				const [day, month, year] = trimmed.split('.')
				return `${day}-${month}-${year}`
			}
			return trimmed
		},

		isDraftModule(module) {
			if (!module) return true
			const hasSchedule =
				(!!module.publishDate && !!module.publishTime) ||
				module.publishType === 'scheduled'
			if (hasSchedule) return false
			const isPublished = module.isPublished ?? module.is_published ?? false
			const draftFlag = module.isDraft ?? module.is_draft ?? false
			return !isPublished || draftFlag
		},

		// Проверка модулей с запланированной публикацией
		checkScheduledPublish() {
			const now = Date.now()

			this.modules.forEach(module => {
				// Пропускаем уже опубликованные модули
				if (module.is_published || module.isPublished) return

				// Проверяем наличие времени автопубликации
				const autoPublishAt =
					module.auto_publish_at ||
					module.autoPublishAt ||
					module.scheduled_publish_at ||
					module.scheduledPublishAt

				if (!autoPublishAt) return

				// Парсим дату публикации
				let publishTime
				if (autoPublishAt.includes('T')) {
					// ISO формат
					publishTime = new Date(autoPublishAt).getTime()
				} else if (module.publishDate && module.publishTime) {
					// Формат из формы
					publishTime = new Date(
						`${module.publishDate}T${module.publishTime}`,
					).getTime()
				} else {
					return
				}

				// Если время публикации наступило, обновляем статус
				if (!isNaN(publishTime) && publishTime <= now) {
					console.log(`📢 Автопубликация модуля: ${module.title}`)
					module.is_published = true
					module.isPublished = true
					module.is_draft = false
					module.isDraft = false
				}
			})
		},

		getPublishedModuleIndex(moduleId) {
			return this.publishedModules.findIndex(module => module.id === moduleId)
		},

		handlePublishedDragStart(module, event) {
			// Отключаем стандартный drag
			if (event) event.preventDefault()
		},

		// === КАСТОМНЫЙ DRAG AND DROP ===

		setCardRef(moduleId, el) {
			if (el) {
				this.cardRefs[moduleId] = el
			}
		},

		startDrag(module, event) {
			if (!module || event.button !== 0) return

			// Не начинаем drag если кликнули по меню или его кнопке
			const target = event.target
			if (
				target.closest('.module-card__menu') ||
				target.closest('.module-card__menu-button') ||
				target.closest('.module-card__dropdown')
			) {
				return
			}

			this.isDragging = true
			this.draggedModule = module
			this.dragStartPos = { x: event.clientX, y: event.clientY }
			this.currentPos = { x: event.clientX - 150, y: event.clientY - 100 }

			document.body.style.cursor = 'grabbing'
			document.body.style.userSelect = 'none'
		},

		handleGlobalMouseMove(event) {
			if (!this.isDragging) return

			this.currentPos = {
				x: event.clientX - 150,
				y: event.clientY - 100,
			}
		},

		handleGlobalMouseUp() {
			if (!this.isDragging) return

			// Находим карточку под курсором
			if (
				this.dragOverModuleId &&
				this.draggedModule &&
				this.dragOverModuleId !== this.draggedModule.id
			) {
				const fromIndex = this.getPublishedModuleIndex(this.draggedModule.id)
				const toIndex = this.getPublishedModuleIndex(this.dragOverModuleId)

				if (fromIndex >= 0 && toIndex >= 0 && fromIndex !== toIndex) {
					this.reorderPublishedModules(fromIndex, toIndex)
				}
			}

			this.endDrag()
		},

		endDrag() {
			this.isDragging = false
			this.draggedModule = null
			this.dragOverModuleId = null

			document.body.style.cursor = ''
			document.body.style.userSelect = ''
		},

		handleCardMouseEnter(module) {
			if (
				this.isDragging &&
				this.draggedModule &&
				module.id !== this.draggedModule.id
			) {
				this.dragOverModuleId = module.id
			}

			if (!this.isDragging) {
				this.scheduleModulePrefetch(module?.id)
			}
		},

		handleCardMouseLeave() {
			// Не сбрасываем сразу, чтобы избежать мерцания
			this.clearModulePrefetchTimer()
		},

		isPersistedModuleId(moduleId) {
			if (moduleId === null || moduleId === undefined) return false
			return !String(moduleId).startsWith('temp_')
		},

		getModuleCacheKey(moduleId) {
			return String(moduleId)
		},

		getCachedModuleBundle(moduleId) {
			const cacheKey = this.getModuleCacheKey(moduleId)
			const entry = this.modulePrefetchCache[cacheKey]
			if (!entry) return null
			if (Date.now() - entry.cachedAt > this.moduleCacheTtlMs) {
				delete this.modulePrefetchCache[cacheKey]
				return null
			}
			return entry.data
		},

		setCachedModuleBundle(moduleId, data) {
			if (!data) return
			const cacheKey = this.getModuleCacheKey(moduleId)
			this.modulePrefetchCache[cacheKey] = {
				cachedAt: Date.now(),
				data,
			}
		},

		invalidateModuleBundleCache(moduleId) {
			if (!this.isPersistedModuleId(moduleId)) return
			const cacheKey = this.getModuleCacheKey(moduleId)
			delete this.modulePrefetchCache[cacheKey]
			delete this.modulePrefetchInFlight[cacheKey]
		},

		clearModulePrefetchTimer() {
			if (this.modulePrefetchTimer) {
				clearTimeout(this.modulePrefetchTimer)
				this.modulePrefetchTimer = null
			}
			this.modulePrefetchTargetId = null
		},

		scheduleModulePrefetch(moduleId) {
			if (!this.isPersistedModuleId(moduleId)) return
			if (
				this.editingModule &&
				String(this.editingModule.id || '') === String(moduleId)
			) {
				return
			}
			if (this.getCachedModuleBundle(moduleId)) return
			if (this.modulePrefetchTargetId === moduleId) return

			this.clearModulePrefetchTimer()
			this.modulePrefetchTargetId = moduleId
			this.modulePrefetchTimer = setTimeout(async () => {
				const targetId = this.modulePrefetchTargetId
				this.clearModulePrefetchTimer()
				try {
					await this.loadModuleDataBundle(targetId, {
						preferCache: true,
					})
				} catch (error) {
					console.warn('Не удалось выполнить prefetch модуля:', error)
				}
			}, this.modulePrefetchDelayMs)
		},

		async loadModuleDataBundle(moduleId, options = {}) {
			const { preferCache = true } = options
			if (!this.isPersistedModuleId(moduleId)) {
				return null
			}

			if (preferCache) {
				const cachedBundle = this.getCachedModuleBundle(moduleId)
				if (cachedBundle) {
					return cachedBundle
				}
			}

			const cacheKey = this.getModuleCacheKey(moduleId)
			if (this.modulePrefetchInFlight[cacheKey]) {
				return this.modulePrefetchInFlight[cacheKey]
			}

			const loaderPromise = (async () => {
				const {
					modulesService,
					lessonsService,
					testsService,
					practicalService,
					homeworkService,
				} = await import('@/services')

				const module = await modulesService.getById(moduleId)

				const loadSection = async (loader, fallback = []) => {
					try {
						const payload = await loader()
						return Array.isArray(payload) ? payload : fallback
					} catch {
						return fallback
					}
				}

				const [lessonsData, testsData, practiceData, homeworkData] =
					await Promise.all([
						loadSection(() => lessonsService.getByModuleId(moduleId, true)),
						// Вопросы теста загружаются лениво при открытии формы теста.
						loadSection(() => testsService.getByModuleId(moduleId, true)),
						loadSection(() => practicalService.getByModuleId(moduleId, true)),
						loadSection(() => homeworkService.getByModuleId(moduleId, true)),
					])

				const bundle = {
					module,
					lessonsData,
					testsData,
					practiceData,
					homeworkData,
				}
				this.setCachedModuleBundle(moduleId, bundle)
				return bundle
			})()
				.catch(error => {
					console.error('Ошибка загрузки данных модуля:', error)
					throw error
				})
				.finally(() => {
					delete this.modulePrefetchInFlight[cacheKey]
				})

			this.modulePrefetchInFlight[cacheKey] = loaderPromise
			return loaderPromise
		},

		handleMouseDown() {
			// Старый метод - не используется
		},

		handleMouseUp() {
			// Старый метод - не используется
		},

		getDragStyle() {
			// Старый метод - не используется
			return {}
		},

		handlePublishedDragOver() {
			// Не используется в кастомном drag
		},

		handlePublishedDragEnter() {
			// Не используется в кастомном drag
		},

		handlePublishedDragLeave() {
			// Не используется в кастомном drag
		},

		handlePublishedDragEnd() {
			// Не используется в кастомном drag
		},

		handlePublishedDrop() {
			// Не используется в кастомном drag
		},

		reorderPublishedModules(fromIndex, toIndex) {
			const reordered = [...this.publishedModules]
			const [moved] = reordered.splice(fromIndex, 1)
			reordered.splice(toIndex, 0, moved)
			this.applyPublishedOrder(reordered)

			// Debounced сохранение для оптимизации
			this.debouncedPersistModulesOrder()
		},

		applyPublishedOrder(reorderedPublished) {
			const publishedQueue = [...reorderedPublished]
			const nextModules = this.modules.map(module =>
				this.isDraftModule(module) ? module : publishedQueue.shift(),
			)
			this.modules.splice(0, this.modules.length, ...nextModules)
		},

		// Debounced версия для оптимизации
		debouncedPersistModulesOrder() {
			if (this._persistTimeout) {
				clearTimeout(this._persistTimeout)
			}

			this._persistTimeout = setTimeout(() => {
				this.persistModulesOrder()
			}, 500)
		},

		async persistModulesOrder() {
			if (!this.courseId) return
			if (this.modules.some(module => String(module.id).startsWith('temp_'))) {
				return
			}

			const moduleIds = this.modules.map(module => module.id)

			try {
				const { modulesService } = await import('@/services')
				await modulesService.reorder(this.courseId, moduleIds)
			} catch (error) {
				console.error('Failed to reorder modules:', error)
			}
		},

		isLogoutRoute(to) {
			if (!to) return false
			const path = to.path || ''
			return (
				path.startsWith('/auth') ||
				path.startsWith('/login') ||
				to.name === 'Auth' ||
				!!(to.meta && to.meta.guestOnly)
			)
		},

		closeEditingSession() {
			this.clearAutoSyncState()
			this.clearEditingStateFromLocalStorage()
			this.showCreationForm = false
			this.resetForm()
		},

		setInitialModuleSnapshot() {
			this.initialModuleSnapshot = this.getCurrentSnapshot()
			this.isModified = false
		},

		getCurrentSnapshot() {
			const snapshot = {
				moduleTitle: this.moduleTitle ? this.moduleTitle.trim() : '',
				moduleDescription: this.moduleDescription || '',
				moduleDuration: this.moduleDuration ?? null,
				completionEnabled: this.completionEnabled,
				completionFinalTestId: this.completionFinalTestId,
				finalTestRequireLessons: this.finalTestRequireLessons,
				finalTestRequirePractice: this.finalTestRequirePractice,
				finalTestRequireHomework: this.finalTestRequireHomework,
				finalTestRequireTests: this.finalTestRequireTests,
				finalTestWindowEnabled: this.finalTestWindowEnabled,
				finalTestWindowDate: this.finalTestWindowDate,
				finalTestWindowTime: this.finalTestWindowTime,
				finalTestWindowDurationMinutes: this.finalTestWindowDurationMinutes,
				coverImage: this.coverImage,
				coverImagePreview: this.coverImagePreview,
				publishType: this.publishType,
				publishDate: this.publishDate,
				publishTime: this.publishTime,
				moduleElements: this.moduleElements,
				deletedElements: this.deletedElements,
			}
			return JSON.stringify(this.normalizeSnapshotValue(snapshot))
		},

		normalizeSnapshotValue(value) {
			if (value instanceof File) {
				return {
					__type: 'file',
					name: value.name,
					size: value.size,
					type: value.type,
					lastModified: value.lastModified,
				}
			}
			if (value instanceof Blob) {
				return {
					__type: 'blob',
					size: value.size,
					type: value.type,
				}
			}
			if (value instanceof Date) {
				return value.toISOString()
			}
			if (Array.isArray(value)) {
				return value.map(item => this.normalizeSnapshotValue(item))
			}
			if (value && typeof value === 'object') {
				const sorted = {}
				Object.keys(value)
					.sort()
					.forEach(key => {
						sorted[key] = this.normalizeSnapshotValue(value[key])
					})
				return sorted
			}
			if (typeof value === 'function') {
				return '[Function]'
			}
			return value
		},

		parseCompletionPolicy(rawPolicy) {
			if (!rawPolicy) return null
			if (typeof rawPolicy === 'string') {
				try {
					return JSON.parse(rawPolicy)
				} catch (error) {
					return null
				}
			}
			return rawPolicy
		},

		parseWindowStartAt(value) {
			if (!value) return { date: '', time: '' }
			const raw = String(value)
			const dateMatch = raw.match(/\d{4}-\d{2}-\d{2}/)
			const timeMatch = raw.match(/\d{2}:\d{2}/)
			return {
				date: dateMatch ? dateMatch[0] : '',
				time: timeMatch ? timeMatch[0] : '',
			}
		},

		composeWindowStartAt() {
			if (!this.finalTestWindowDate || !this.finalTestWindowTime) return ''
			return `${this.finalTestWindowDate}T${this.finalTestWindowTime}`
		},

		applyCompletionPolicy(rawPolicy) {
			const policy = this.parseCompletionPolicy(rawPolicy)
			const finalTestId =
				policy?.final_test_id ?? policy?.finalTestId ?? null

			this.completionEnabled = !!finalTestId
			this.completionFinalTestId = finalTestId ? String(finalTestId) : ''
			this.completionCapPercent = 95
			this.finalTestRequireLessons = false
			this.finalTestRequirePractice = false
			this.finalTestRequireHomework = false
			this.finalTestRequireTests = false
			this.finalTestWindowEnabled = false
			this.finalTestWindowDate = ''
			this.finalTestWindowTime = ''
			this.finalTestWindowDurationMinutes = 120

			const accessRules = policy?.access_rules ?? policy?.accessRules ?? null
			if (!accessRules) return

			const requireAll =
				accessRules?.require_all_items ?? accessRules?.requireAllItems
			const requireItems =
				accessRules?.require_items ?? accessRules?.requireItems ?? null
			if (requireAll) {
				this.finalTestRequireLessons = true
				this.finalTestRequirePractice = true
				this.finalTestRequireHomework = true
				this.finalTestRequireTests = true
			} else if (Array.isArray(requireItems)) {
				const normalized = requireItems.map(item =>
					String(item || '').trim().toLowerCase(),
				)
				this.finalTestRequireLessons = normalized.includes('lessons')
				this.finalTestRequirePractice = normalized.some(value =>
					['practice', 'practical', 'practicals'].includes(value),
				)
				this.finalTestRequireHomework = normalized.some(value =>
					['homework', 'homeworks'].includes(value),
				)
				this.finalTestRequireTests = normalized.some(value =>
					['test', 'tests'].includes(value),
				)
			} else if (requireItems && typeof requireItems === 'object') {
				this.finalTestRequireLessons = Boolean(requireItems.lessons)
				this.finalTestRequirePractice = Boolean(requireItems.practice)
				this.finalTestRequireHomework = Boolean(requireItems.homework)
				this.finalTestRequireTests = Boolean(requireItems.tests)
			}

			const timeWindow =
				accessRules?.time_window ?? accessRules?.timeWindow ?? null
			const startAt = timeWindow?.start_at ?? timeWindow?.startAt ?? ''
			const duration = Number(
				timeWindow?.duration_minutes ?? timeWindow?.durationMinutes ?? 0,
			)
			if (startAt) {
				const parts = this.parseWindowStartAt(startAt)
				this.finalTestWindowDate = parts.date
				this.finalTestWindowTime = parts.time
			}
			if (Number.isFinite(duration) && duration > 0) {
				this.finalTestWindowDurationMinutes = duration
			}
			this.finalTestWindowEnabled = !!(
				(this.finalTestWindowDate && this.finalTestWindowTime) ||
				(Number.isFinite(duration) && duration > 0)
			)
		},
		buildCompletionAccessRules() {
			if (!this.completionEnabled) {
				return { accessRules: null }
			}
			const requireItems = {
				lessons: this.finalTestRequireLessons,
				practice: this.finalTestRequirePractice,
				homework: this.finalTestRequireHomework,
				tests: this.finalTestRequireTests,
			}
			const hasRequireItems = Object.values(requireItems).some(Boolean)
			const duration = Number(this.finalTestWindowDurationMinutes) || 0
			const windowStartAt = this.composeWindowStartAt()
			const hasWindow =
				this.finalTestWindowEnabled && windowStartAt && duration > 0
			if (this.finalTestWindowEnabled && !hasWindow) {
				return {
					error: 'Заполните дату и длительность окна доступа.',
				}
			}
			const accessRules = {}
			if (hasRequireItems) {
				accessRules.require_items = requireItems
			}
			if (hasWindow) {
				accessRules.time_window = {
					start_at: windowStartAt,
					duration_minutes: duration,
				}
			}
			return {
				accessRules: Object.keys(accessRules).length ? accessRules : null,
			}
		},
		//  ОБРАБОТЧИК BEFOREUNLOAD - Блокирует закрытие/перезагрузку вкладки
		handleBeforeUnload(event) {
			if (this.showCreationForm) {
				const message =
					'У вас есть несохраненные изменения. Вы уверены, что хотите покинуть страницу?'
				event.preventDefault()
				// Для старых браузеров
				event.returnValue = message
				return message
			}
		},

		//  Проверка возможности покинуть страницу (для программного использования)
		canLeavePage() {
			if (!this.showCreationForm) {
				return true
			}

			const confirmMessage = this.hasRealChanges
				? 'У вас есть несохраненные изменения в модуле. Вы уверены, что хотите покинуть страницу?'
				: 'Вы находитесь в режиме редактирования модуля. Вы уверены, что хотите выйти?'

			return confirm(confirmMessage)
		},

		//  Метод для безопасной навигации (можно использовать в других компонентах)
		async safeNavigate(route) {
			if (this.canLeavePage()) {
				this.clearEditingStateFromLocalStorage()
				this.$router.push(route)
			}
		},

		//  Хелпер для конвертации даты DD-MM-YYYY -> YYYY-MM-DD
		convertDateToISO(dateString) {
			if (!dateString) return ''
			// Проверяем формат DD-MM-YYYY
			if (dateString.match(/^\d{2}-\d{2}-\d{4}$/)) {
				const [day, month, year] = dateString.split('-')
				return `${year}-${month}-${day}`
			}
			// Уже в формате ISO
			return dateString
		},

		//  Хелпер для конвертации даты YYYY-MM-DD -> DD-MM-YYYY
		convertDateFromISO(dateString) {
			if (!dateString) return ''
			// Проверяем формат YYYY-MM-DD
			if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
				const [year, month, day] = dateString.split('-')
				return `${day}-${month}-${year}`
			}
			// Уже в нужном формате
			return dateString
		},

		// Загрузка модуля для редактирования
		async loadModuleForEdit(moduleId) {
			const requestId = ++this.moduleLoadRequestId
			const isStaleRequest = () => requestId !== this.moduleLoadRequestId

			try {
				console.log('Загрузка модуля для редактирования:', moduleId)

				// Не сбрасываем модель при загрузке того же модуля: это предотвращает мигание UI.
				const shouldResetEditingModule =
					!this.editingModule ||
					this.editingModule.id === undefined ||
					this.editingModule.id === null ||
					String(this.editingModule.id) !== String(moduleId)
				if (shouldResetEditingModule) {
					this.editingModule = null
				}

				const bundle = await this.loadModuleDataBundle(moduleId, {
					preferCache: true,
				})
				if (!bundle) {
					throw new Error('Не удалось получить данные модуля')
				}
				const {
					module,
					lessonsData,
					testsData,
					practiceData,
					homeworkData,
				} = bundle
				if (isStaleRequest()) return
				console.log('Модуль загружен:', module)

				// Показываем форму сразу после загрузки базовых данных модуля.
				const normalizedPublish = this.normalizeModulePublishDateTime(module)
				const normalizedPublishDate = normalizedPublish.publishDate || ''
				const normalizedPublishTime = normalizedPublish.publishTime || ''
				const normalizedPublishType =
					module.publish_type ||
					(normalizedPublishDate && normalizedPublishTime
						? 'scheduled'
						: 'immediate')

				this.isRestoringModuleData = true
				this.isModuleContentLoading = true
				this.showCreationForm = true
				this.editingModule = {
					id: module.id,
					title: module.title,
					subtitle: module.subtitle || '',
					coverImage: module.cover_image,
					publishType: normalizedPublishType,
					publishDate: normalizedPublishDate,
					publishTime: normalizedPublishTime,
					publishInfo: module.publish_info || '\u0421\u0440\u0430\u0437\u0443',
					isDraft: module.is_draft || false,
					isHidden: module.is_hidden || false,
				}
				this.moduleTitle = module.title
				this.moduleDescription = module.description || ''
				this.moduleDuration = module.duration_hours || null
				this.applyCompletionPolicy(
					module.completion_policy || module.completionPolicy,
				)
				// Не перезаписываем coverImage (File), но обновляем превью из БД.
				this.coverImagePreview = module.cover_image
				this.publishType = normalizedPublishType
				this.publishDate = normalizedPublishDate
				this.publishTime = normalizedPublishTime
				this.deletedElements = {
					lessons: [],
					tests: [],
					homework: [],
					practice: [],
				}
				this.moduleElements = {
					lessons: [],
					tests: [],
					homework: [],
					practice: [],
				}

				console.log('📚 Материалы модуля получены (cache/network):', {
					lessons: Array.isArray(lessonsData) ? lessonsData.length : 0,
					tests: Array.isArray(testsData) ? testsData.length : 0,
					practice: Array.isArray(practiceData) ? practiceData.length : 0,
					homework: Array.isArray(homeworkData) ? homeworkData.length : 0,
				})
				if (isStaleRequest()) return

				// Преобразуем материалы в формат для moduleElements
				this.moduleElements = {
					lessons: (lessonsData || []).map(lesson => {
						//  Парсим additional_materials если это JSON-строка
						let parsedMaterials = lesson.additional_materials || []
						if (typeof parsedMaterials === 'string') {
							try {
								parsedMaterials = JSON.parse(parsedMaterials)
							} catch {
								parsedMaterials = []
							}
						}
						if (!Array.isArray(parsedMaterials)) {
							parsedMaterials = []
						}

						// Вычисляем поля автопубликации из scheduled_publish_at
						const scheduledAt = lesson.scheduled_publish_at || null
						let publishDate = ''
						let publishTime = ''
						if (scheduledAt) {
							if (scheduledAt.includes('T')) {
								// ISO формат из БД "2025-12-24T11:30:00.000Z"
								const date = new Date(scheduledAt)
								const year = date.getFullYear()
								const month = String(date.getMonth() + 1).padStart(2, '0')
								const day = String(date.getDate()).padStart(2, '0')
								const hours = String(date.getHours()).padStart(2, '0')
								const minutes = String(date.getMinutes()).padStart(2, '0')
								publishDate = `${year}-${month}-${day}`
								publishTime = `${hours}:${minutes}`
							} else {
								// Формат "2025-12-23 12:30"
								const parts = scheduledAt.split(' ')
								publishDate = parts[0] || ''
								publishTime = parts[1] ? parts[1].slice(0, 5) : ''
							}
						}
						const inferredHidden =
							!!scheduledAt ||
							(lesson.is_draft !== true && lesson.is_published === false)

						return {
							id: lesson.id,
							title: lesson.title,
							description: lesson.description || '',
							type: lesson.lesson_type || 'video',
							duration: lesson.duration_minutes || 30,
							isDraft: lesson.is_draft ?? lesson.isDraft ?? false,
							is_hidden: lesson.is_hidden ?? lesson.isHidden ?? inferredHidden,
							isHidden: lesson.is_hidden ?? lesson.isHidden ?? inferredHidden,
							scheduled_publish_at: scheduledAt,
							fullData: {
								...lesson,
								type: lesson.lesson_type || 'video',
								title: lesson.title,
								description: lesson.description || '',
								duration: lesson.duration_minutes || 30,
								video_url: lesson.video_url || '',
								content: lesson.article_content || '',
								materials: parsedMaterials,
								is_published:
									lesson.is_published !== undefined
										? lesson.is_published
										: true,
								// Поля автопубликации
								scheduled_publish_at: scheduledAt,
								publishType: scheduledAt ? 'scheduled' : 'immediate',
								publishDate: publishDate,
								publishTime: publishTime,
							},
						}
					}),
					tests: (testsData || []).map(test => {
						// Вычисляем поля автопубликации из scheduled_publish_at
						const scheduledAt = test.scheduled_publish_at || null
						let publishDate = ''
						let publishTime = ''
						if (scheduledAt) {
							if (scheduledAt.includes('T')) {
								// ISO формат из БД "2025-12-24T11:30:00.000Z"
								const date = new Date(scheduledAt)
								const year = date.getFullYear()
								const month = String(date.getMonth() + 1).padStart(2, '0')
								const day = String(date.getDate()).padStart(2, '0')
								const hours = String(date.getHours()).padStart(2, '0')
								const minutes = String(date.getMinutes()).padStart(2, '0')
								publishDate = `${year}-${month}-${day}`
								publishTime = `${hours}:${minutes}`
							} else {
								// Формат "2025-12-23 12:30"
								const parts = scheduledAt.split(' ')
								publishDate = parts[0] || ''
								publishTime = parts[1] ? parts[1].slice(0, 5) : ''
							}
						}

						return {
							id: test.id,
							title: test.title,
							description: test.description || '',
							//  Добавляем поля для MaterialCard
							duration: test.duration_minutes ?? 0,
							passingScore: test.passing_score ?? 70,
							attempts: test.max_attempts ?? 1,
							//  Проверяем questions_count из БД, затем длину массива questions
							questionsCount:
								test.questions_count ?? test.questions?.length ?? 0,
							questions: test.questions || [],
							scheduled_publish_at: scheduledAt,
							// ✅ is_hidden = true если есть scheduled_publish_at
							is_hidden: test.is_hidden ?? !!scheduledAt,
							isHidden: test.is_hidden ?? !!scheduledAt,
							fullData: {
								...test,
								// Поля для формы редактирования
								duration: test.duration_minutes ?? 0,
								passingScore: test.passing_score ?? 70,
								attempts: test.max_attempts ?? 1,
								randomOrder: test.random_order ?? false,
								showCorrectAnswers: test.show_correct_answers ?? true,
								allowReview: test.allow_review ?? true,
								penalizeWrong: test.penalize_wrong ?? false,
								penaltyPercentage: test.penalty_percentage ?? 50,
								disableCopying: test.disable_copying ?? false,
								limitTimePerQuestion: test.limit_time_per_question ?? false,
								timePerQuestion: test.time_per_question ?? 0,
								selectRandomQuestions: test.select_random_questions ?? false,
								questionsToShow: test.questions_to_show ?? 0,
								questions: test.questions || [],
								// Поля автопубликации
								scheduled_publish_at: scheduledAt,
								isScheduledPublish: !!scheduledAt,
								autoPublish: !scheduledAt,
								publishDate: publishDate,
								publishTime: publishTime,
								is_hidden: test.is_hidden ?? !!scheduledAt,
								is_published: test.is_published ?? !scheduledAt,
							},
							isDraft: test.is_draft ?? test.isDraft ?? false,
						}
					}),
					practice: (practiceData || []).map(practice => {
						//  Преобразуем mini_test (из БД) в miniTest (для формы)
						const fullData = { ...practice }
						if (practice.mini_test) {
							fullData.miniTest = practice.mini_test
							fullData.includeMiniTest = true
						}
						//  Убеждаемся, что materials - это массив
						if (!fullData.materials || !Array.isArray(fullData.materials)) {
							fullData.materials = []
						}
						const inferredHidden =
							practice.is_draft !== true && practice.is_published === false
						return {
							id: practice.id,
							title: practice.title,
							description: practice.description || '',
							fullData: fullData,
							isDraft: practice.is_draft ?? practice.isDraft ?? false,
							is_hidden: practice.is_hidden ?? practice.isHidden ?? inferredHidden,
							isHidden: practice.is_hidden ?? practice.isHidden ?? inferredHidden,
						}
					}),
					homework: (homeworkData || []).map(homework => {
						//  Парсим requirements если это JSON-строка
						let parsedRequirements = homework.requirements || []
						if (typeof parsedRequirements === 'string') {
							try {
								parsedRequirements = JSON.parse(parsedRequirements)
							} catch {
								parsedRequirements = parsedRequirements
									? [parsedRequirements]
									: []
							}
						}
						//  Парсим materials если это JSON-строка
						let parsedMaterials = homework.materials || []
						if (typeof parsedMaterials === 'string') {
							try {
								parsedMaterials = JSON.parse(parsedMaterials)
							} catch {
								parsedMaterials = []
							}
						}
						//  Форматируем дату в YYYY-MM-DD
						let formattedDueDate = homework.due_date || null
						if (formattedDueDate && formattedDueDate.includes('T')) {
							formattedDueDate = formattedDueDate.split('T')[0]
						}
						//  Форматируем время в HH:MM
						let formattedDueTime = homework.due_time || null
						if (formattedDueTime && formattedDueTime.length > 5) {
							formattedDueTime = formattedDueTime.substring(0, 5)
						}
						const inferredHidden =
							homework.is_draft !== true && homework.is_published === false
						return {
							id: homework.id,
							title: homework.title,
							description: homework.description || '',
							due_date: formattedDueDate,
							due_time: formattedDueTime,
							max_score: homework.max_score ?? null,
							allow_late_submission: homework.allow_late_submission ?? false,
							requirements: parsedRequirements,
							materials: parsedMaterials,
							fullData: {
								...homework,
								requirements: parsedRequirements,
								materials: parsedMaterials,
								dueDate: formattedDueDate,
								dueTime: formattedDueTime,
								maxScore: homework.max_score ?? null,
								allowLateSubmission: homework.allow_late_submission ?? false,
							},
							isDraft: homework.is_draft ?? homework.isDraft ?? false,
							is_hidden: homework.is_hidden ?? homework.isHidden ?? inferredHidden,
							isHidden: homework.is_hidden ?? homework.isHidden ?? inferredHidden,
						}
					}),
				}

				console.log(
					'✅ Материалы загружены в moduleElements:',
					this.moduleElements,
				)
				if (isStaleRequest()) return

				this.isRestoringModuleData = false
				this.setInitialModuleSnapshot()

				console.log('Форма заполнена данными модуля')

				//  Сохраняем состояние после загрузки модуля
				this.saveEditingStateToLocalStorage()

				//  Автоскролл до формы редактирования модуля
				this.$nextTick(() => {
					const formElement = document.getElementById('module-edit-form')
					if (formElement) {
						formElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
					}
				})
			} catch (error) {
				if (isStaleRequest()) {
					return
				}
				console.error('Ошибка загрузки модуля для редактирования:', error)
				alert('Не удалось загрузить модуль для редактирования')
			} finally {
				if (!isStaleRequest()) {
					this.isModuleContentLoading = false
					this.isRestoringModuleData = false
				}
			}
		},

		// Показать форму создания модуля
		handleShowCreationForm() {
			this.showCreationForm = true
			this.$emit('form-opened')
			this.resetForm()
			//  Создаем временный объект модуля с временным ID для нового модуля (ПОСЛЕ resetForm!)
			this.editingModule = {
				id: `temp_${Date.now()}`, // Временный ID для нового модуля (с префиксом temp_)
			}
			this.setInitialModuleSnapshot()

			//  Сохраняем состояние после открытия формы создания
			this.saveEditingStateToLocalStorage()

			//  Автоскролл до формы создания модуля
			this.$nextTick(() => {
				const formElement = document.getElementById('module-edit-form')
				if (formElement) {
					formElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
				}
			})
		},

		// Редактирование модуля
		handleEditModule(data) {
			// Эмитим событие в родительский компонент
			this.$emit('edit-module', data)
		},
		handlePreviewOpening() {
			if (this.hasRealChanges) {
				this.previewDirtyGuardActive = false
				return
			}

			this.previewDirtyGuardActive = true
		},
		handlePreviewIntentCapture(event) {
			if (event?.button !== undefined && event.button !== 0) return

			const trigger = event?.target?.closest?.('button, [role="button"], a')
			if (!trigger) return

			const className = Array.from(trigger.classList || []).join(' ').toLowerCase()
			const label = [
				trigger.textContent,
				trigger.getAttribute?.('title'),
				trigger.getAttribute?.('aria-label'),
			]
				.filter(Boolean)
				.join(' ')
				.toLowerCase()

			const isPreviewTrigger =
				className.includes('preview') || label.includes('предпросмотр')

			if (!isPreviewTrigger) return

			this.handlePreviewOpening()
		},
		handlePreviewClosed() {
			console.log('[PreviewClosed] called, previewDirtyGuardActive:', this.previewDirtyGuardActive)
			
			if (!this.previewDirtyGuardActive) return

			// Устанавливаем флаг для восстановления состояния при возврате
			sessionStorage.setItem('preview_recovery_mode', 'true')

			// В Bitrix режиме закрываем форму редактирования и переключаем на вкладку модулей
			const isBitrix = localStorage.getItem('bitrix24_mode') === 'true'
			console.log('[PreviewClosed] isBitrix:', isBitrix)
			
			if (isBitrix) {
				console.log('[PreviewClosed] emitting close-editing-form')
				this.$emit('close-editing-form')
			}

			// НЕ обновляем снимок при закрытии предпросмотра
			// Снимок должен оставаться таким же, как при открытии формы редактирования
			// this.setInitialModuleSnapshot()
			this.saveEditingStateToLocalStorage()
			this.previewDirtyGuardActive = false
		},
		handlePreviewReturnFocus() {
			if (!this.previewDirtyGuardActive) return

			setTimeout(() => {
				this.handlePreviewClosed()
			}, 80)
		},
		handlePreviewCurrentModule() {
			if (!this.editingModule?.id) return
			this.handlePreviewOpening()
			this.openModulePreview(this.editingModule.id)
		},
		handlePreviewModule(data) {
			const moduleId = data?.id
			if (!moduleId) return
			this.openModulePreview(moduleId)
		},
		openModulePreview(moduleId) {
			if (!moduleId || String(moduleId).startsWith('temp_')) {
				alert('Сначала сохраните модуль, затем откройте предпросмотр.')
				return
			}

			// Устанавливаем флаг для Bitrix, что открываем preview
			const isBitrix = localStorage.getItem('bitrix24_mode') === 'true'
			if (isBitrix) {
				sessionStorage.setItem('bitrix_preview_opened', 'true')
				console.log('[Bitrix] Флаг preview установлен')
			}

			openInternalRouteInContext(this.$router, {
				name: 'TeacherScreen8Preview',
				query: {
					courseId: this.courseId ? String(this.courseId) : undefined,
					editorContext: this.$route.path.startsWith('/admin/') ? 'admin' : 'teacher',
					moduleId: String(moduleId),
					previewType: 'module',
					testMode: '1',
				},
			}, {
				title: 'Предпросмотр модуля',
				leftBoundary: 48,
				closeCallback: () => this.handlePreviewClosed(),
			})
		},

		// Переключение видимости модуля
		handleToggleModuleVisibility(data) {
			// Эмитим событие в родительский компонент
			this.$emit('toggle-visibility', data)
		},

		// Удаление модуля
		handleDeleteModule(data) {
			// Эмитим событие в родительский компонент
			this.$emit('delete-module', data)
		},

		handleUploadCover() {
			const input = document.createElement('input')
			input.type = 'file'
			input.accept = 'image/jpeg,image/jpg,image/png,image/webp,image/gif'

			input.onchange = async e => {
				const file = e.target.files[0]
				if (file) {
					// Проверяем размер файла (максимум 5MB)
					const maxSize = 5 * 1024 * 1024 // 5MB в байтах
					if (file.size > maxSize) {
						alert('Размер файла не должен превышать 5MB')
						return
					}

					const previousPreview = this.coverImagePreview
					const nextPreview = URL.createObjectURL(file)

					// Сначала обновляем ссылку в состоянии, потом освобождаем старую.
					this.coverImagePreview = nextPreview
					if (previousPreview && previousPreview !== nextPreview) {
						this.deferRevokeBlobUrl(previousPreview)
					}
					this.coverImage = file
					this.refreshModificationState()
					this.$emit('cover-uploaded', file)
					console.log('Обложка загружена:', file.name)
				}
			}

			input.click()
		},

		// Удаление обложки
		handleRemoveCover() {
			const previousPreview = this.coverImagePreview
			this.coverImage = null
			this.coverImagePreview = null
			this.deferRevokeBlobUrl(previousPreview)
			this.refreshModificationState()
			console.log('Обложка удалена')
		},

		handleTitleChange() {
			this.refreshModificationState()
			this.$emit('title-changed', this.moduleTitle)
		},

		handleDescriptionChange() {
			this.refreshModificationState()
		},

		handleDurationChange() {
			this.refreshModificationState()
		},

		handlePublishTypeChange() {
			this.refreshModificationState()
			this.$emit('publish-type-changed', this.publishType)
		},

		handleDateChange() {
			this.refreshModificationState()
			this.$emit('publish-date-changed', this.publishDate)
		},

		handleTimeChange() {
			this.refreshModificationState()
			this.$emit('publish-time-changed', this.publishTime)
		},

		handleSaveSettings() {
			const settings = {
				publishType: this.publishType,
				publishDate: this.publishDate,
				publishTime: this.publishTime,
			}

			console.log('Saving auto-publish settings:', settings)
			this.$emit('settings-saved', settings)
		},

		handleSaveModule() {
			this.clearAutoSyncState()
			if (!this.isFormValid) {
				alert('Пожалуйста, заполните все обязательные поля')
				return
			}

			if (this.completionEnabled && !this.completionFinalTestId) {
				alert(
					'Выберите финальный тест для завершения модуля.',
				)
				return
			}

			// Проверка наличия всех элементов
			if (!this.canSaveModule) {
				this.showValidationWarning = true
				alert(
					'Для публикации модуля необходимо создать минимум по одному элементу каждого типа: уроки, тесты, домашние работы и практические задания.',
				)
				return
			}

			let isHidden = false
			if (this.publishType === 'scheduled') {
				const publishAt = new Date(`${this.publishDate}T${this.publishTime}`)
				if (!Number.isNaN(publishAt.getTime())) {
					isHidden = publishAt.getTime() > Date.now()
				} else {
					isHidden = true
				}
			}
			const immediateLabel = '\u0421\u0440\u0430\u0437\u0443'
			const publishDateForInfo = this.formatDateForInfo(this.publishDate)
			const {
				accessRules,
				error: accessRulesError,
			} = this.buildCompletionAccessRules()
			if (accessRulesError) {
				alert(accessRulesError)
				return
			}
			const completionPolicy = this.completionEnabled
				? {
						final_test_id: Number(this.completionFinalTestId),
						cap_percent: 95,
						...(accessRules ? { access_rules: accessRules } : {}),
				  }
				: null

			const moduleData = {
				id: this.editingModule?.id || `temp_${Date.now()}`,
				title: this.moduleTitle,
				subtitle: '',
				description: this.moduleDescription || '',
				duration_hours: this.moduleDuration || null,
				coverImage: this.coverImage,
				publishType: this.publishType,
				publishDate: this.publishDate,
				publishTime: this.publishTime,
				publishInfo:
					this.publishType === 'immediate'
						? immediateLabel
						: `${publishDateForInfo} ${this.publishTime}`,
				isDraft: false,
				// Scheduled modules are published but hidden until the publish time
				isHidden: isHidden,
				elements: { ...this.moduleElements },
				completion_policy: completionPolicy,
				//  Добавляем список удаленных элементов
				deletedElements: { ...this.deletedElements },
			}
			moduleData.__onSaveSuccess = result => {
				const savedModuleId = result?.id || this.editingModule?.id || moduleData.id
				this.invalidateModuleBundleCache(savedModuleId)
				if (!this.isEditingExistingModule) return
				this.clearEditingStateFromLocalStorage()
				this.showCreationForm = false
				this.resetForm()
			}
			moduleData.__onSaveError = () => {}

			if (this.editingModule) {
				// Обновление существующего модуля
				console.log('Updating module:', moduleData)
				this.$emit('save-module', moduleData)
			} else {
				// Создание нового модуля
				console.log('Creating module:', moduleData)
				this.$emit('save-module', moduleData)
			}


		},

		handleSaveAsDraft() {
			this.clearAutoSyncState()
			if (!this.moduleTitle.trim()) {
				alert('Пожалуйста, введите название модуля')
				return
			}

			if (this.completionEnabled && !this.completionFinalTestId) {
				alert(
					'Выберите финальный тест для завершения модуля.',
				)
				return
			}

			const {
				accessRules,
				error: accessRulesError,
			} = this.buildCompletionAccessRules()
			if (accessRulesError) {
				alert(accessRulesError)
				return
			}
			const completionPolicy = this.completionEnabled
				? {
						final_test_id: Number(this.completionFinalTestId),
						cap_percent: 95,
						...(accessRules ? { access_rules: accessRules } : {}),
				  }
				: null

			const moduleData = {
				id: this.editingModule?.id || `temp_${Date.now()}`,
				title: this.moduleTitle,
				subtitle: '',
				description: this.moduleDescription || '',
				duration_hours: this.moduleDuration || null,
				coverImage: this.coverImage,
				publishType: this.publishType,
				publishDate: this.publishDate,
				publishTime: this.publishTime,
				publishInfo: 'Черновик',
				isDraft: true,
				isHidden: false,
				elements: { ...this.moduleElements },
				completion_policy: completionPolicy,
				//  Добавляем список удаленных элементов
				deletedElements: { ...this.deletedElements },
			}
			moduleData.__onSaveSuccess = result => {
				const savedModuleId = result?.id || this.editingModule?.id || moduleData.id
				this.invalidateModuleBundleCache(savedModuleId)
				this.clearEditingStateFromLocalStorage()
				this.showCreationForm = false
				this.resetForm()
			}
			moduleData.__onSaveError = () => {}

			//  Детальное логирование для отладки
			console.log('🔍 [handleSaveAsDraft] Сохранение черновика:', {
				isEditing: !!this.editingModule,
				editingModuleId: this.editingModule?.id,
				coverImage: this.coverImage,
				coverImageType: this.coverImage?.constructor?.name,
				isFile: this.coverImage instanceof File,
				coverImagePreview: this.coverImagePreview,
				moduleData: moduleData,
			})

			if (this.editingModule) {
				// Обновление существующего черновика
				const index = this.modules.findIndex(
					m => m.id === this.editingModule.id,
				)
				if (index !== -1) {
					//  Модуль найден в массиве - обновляем
					this.modules.splice(index, 1, moduleData)
					console.log('📝 Обновление существующего черновика:', moduleData)
					this.$emit('draft-updated', moduleData)
				} else {
					//  Модуль НЕ найден (новый черновик с временным ID) - добавляем
					this.modules.push(moduleData)
					console.log('✨ Создание нового черновика:', moduleData)
					this.$emit('save-draft', moduleData)
				}
			} else {
				// Создание нового черновика (на случай если editingModule === null)
				this.modules.push(moduleData)
				console.log('✨ Создание нового черновика:', moduleData)
				this.$emit('save-draft', moduleData)
			}


		},

		handleCancel() {
			this.clearAutoSyncState()
			if (this.mustApplyChangesBeforeExit) {
				alert(
					'Сначала нажмите "Применить изменения". Выход из редактирования с несохраненными изменениями заблокирован.'
				)
				return
			}
			if (this.hasRealChanges) {
				if (
					!confirm(
						'У вас есть несохраненные изменения. Вы уверены, что хотите отменить?',
					)
				) {
					return
				}
			}

			//  Очищаем localStorage при отмене
			this.clearEditingStateFromLocalStorage()

			this.showCreationForm = false
			this.resetForm()
			this.$emit('cancel')
		},

		resetForm() {
			this.clearAutoSyncState()
			this.clearModulePrefetchTimer()
			this.isAutoSyncInProgress = false
			this.isModuleContentLoading = false
			this.isRestoringModuleData = false
			const previousPreview = this.coverImagePreview

			this.editingModule = null
			this.moduleTitle = ''
			this.moduleDescription = ''
			this.moduleDuration = null
			this.completionEnabled = false
			this.completionFinalTestId = ''
			this.completionCapPercent = 95
			this.finalTestRequireLessons = false
			this.finalTestRequirePractice = false
			this.finalTestRequireHomework = false
			this.finalTestRequireTests = false
			this.finalTestWindowEnabled = false
			this.finalTestWindowDate = ''
			this.finalTestWindowTime = ''
			this.finalTestWindowDurationMinutes = 120
			this.coverImage = null
			this.coverImagePreview = null
			this.deferRevokeBlobUrl(previousPreview)
			this.publishType = 'scheduled'
			this.publishDate = ''
			this.publishTime = ''
			this.isDraft = false
			this.isModified = false
			this.showValidationWarning = false
			this.initialModuleSnapshot = null
			// Сброс элементов модуля
			this.moduleElements = {
				lessons: [],
				tests: [],
				homework: [],
				practice: [],
			}
			//  Сброс удаленных элементов
			this.deletedElements = {
				lessons: [],
				tests: [],
				homework: [],
				practice: [],
			}
		},

		// Конвертация File в base64
		async fileToBase64(file) {
			return new Promise((resolve, reject) => {
				const reader = new FileReader()
				reader.onload = () => resolve(reader.result)
				reader.onerror = error => reject(error)
				reader.readAsDataURL(file)
			})
		},

		// Конвертация base64 в Blob URL
		base64ToBlobUrl(base64) {
			try {
				// Извлекаем MIME тип и данные
				const parts = base64.split(',')
				const mimeMatch = parts[0].match(/:(.*?);/)
				if (!mimeMatch) return null

				const mime = mimeMatch[1]
				const bstr = atob(parts[1])
				let n = bstr.length
				const u8arr = new Uint8Array(n)

				while (n--) {
					u8arr[n] = bstr.charCodeAt(n)
				}

				const blob = new Blob([u8arr], { type: mime })
				return URL.createObjectURL(blob)
			} catch (error) {
				console.error('Ошибка конвертации base64 в blob URL:', error)
				return null
			}
		},

		syncFinalTestIdForTestElement(element) {
			if (!element) return
			const tempId = element.tempId
			if (!tempId) return
			if (String(this.completionFinalTestId) === String(tempId)) {
				this.completionFinalTestId = String(element.id)
			}
		},

		// Обработчики событий от ModuleTestsSection
		handleElementCreated(data) {
			console.log('🎯 handleElementCreated вызван:', data)
			const { type, element } = data

			if (!this.moduleElements[type]) {
				console.error(`❌ Тип ${type} не найден в moduleElements!`)
				return
			}

			//  Проверяем, нет ли уже элемента с таким ID
			const existingIndex = this.moduleElements[type].findIndex(
				e => e.id === element.id,
			)

			if (existingIndex !== -1) {
				// Элемент уже существует - обновляем вместо добавления
				console.log(
					`⚠️ Элемент ${type} с ID ${element.id} уже существует, обновляем`,
				)
				this.moduleElements[type].splice(existingIndex, 1, element)
			} else {
				//  Дополнительная проверка по title для временных ID
				const duplicateByTitle = this.moduleElements[type].findIndex(
					e =>
						e.title === element.title &&
						String(e.id).startsWith('temp_') !==
							String(element.id).startsWith('temp_'),
				)

				if (duplicateByTitle !== -1) {
					console.log(`⚠️ Найден дубликат по title, заменяем`)
					this.moduleElements[type].splice(duplicateByTitle, 1, element)
				} else {
					// Добавляем новый элемент
					this.moduleElements[type].push(element)
					console.log(`✅ Element created: ${type}`, element)
				}
			}

			if (type === 'tests') {
				this.syncFinalTestIdForTestElement(element)
			}
			this.refreshModificationState()
			console.log(
				'📊 Обновленное состояние moduleElements:',
				this.moduleElements,
			)
			this.scheduleAutoSync('element-created')
		},

		handleElementUpdated(data) {
			const { type, element } = data

			if (!this.moduleElements[type]) {
				console.error(`❌ Тип ${type} не найден в moduleElements!`)
				return
			}

			//  Ищем элемент по ID
			const index = this.moduleElements[type].findIndex(
				e => e.id === element.id,
			)

			if (index !== -1) {
				// Обновляем существующий элемент
				this.moduleElements[type].splice(index, 1, element)
				if (type === 'tests') {
					this.syncFinalTestIdForTestElement(element)
				}
				this.refreshModificationState()
				console.log(`✅ Element updated: ${type}`, element)
				this.scheduleAutoSync('element-updated')
				return
			}

			//  Если не нашли по ID, ищем по временному ID или title
			// Это нужно когда временный ID заменяется на реальный
			const tempIndex = this.moduleElements[type].findIndex(e => {
				// Проверяем, не является ли это тем же элементом с другим ID
				const isSameByTempId =
					String(e.id).startsWith('temp_') && element.tempId === e.id
				const isSameByTitle =
					e.title === element.title &&
					!this.moduleElements[type].some(other => other.id === element.id)
				return isSameByTempId || isSameByTitle
			})

			if (tempIndex !== -1) {
				// Заменяем старый элемент новым
				console.log(`🔄 Заменяем элемент (смена ID): ${type}`, element)
				this.moduleElements[type].splice(tempIndex, 1, element)
				if (type === 'tests') {
					this.syncFinalTestIdForTestElement(element)
				}
			} else {
				// Добавляем как новый (но проверяем на дубликат по ID еще раз)
				const alreadyExists = this.moduleElements[type].some(
					e => e.id === element.id,
				)

				if (!alreadyExists) {
					this.moduleElements[type].push(element)
					console.log(`➕ Element added: ${type}`, element)
				} else {
					console.log(`⚠️ Элемент ${element.id} уже существует, пропускаем`)
				}
			}

			this.refreshModificationState()
			this.scheduleAutoSync('element-updated')
		},

		handleElementDeleted(data) {
			const { type, elementId } = data
			if (this.moduleElements[type]) {
				const index = this.moduleElements[type].findIndex(
					e => e.id === elementId,
				)
				if (index !== -1) {
					this.moduleElements[type].splice(index, 1)
					console.log(`Element deleted: ${type}`, elementId)
				}
			}

			//  Если элемент реальный (из БД), добавляем его в список удаленных
			const isRealElement = elementId < 1000000000000
			if (isRealElement && this.editingModule) {
				console.log(
					`🗑️ Добавление элемента в список удаленных: ${type}`,
					elementId,
				)
				this.deletedElements[type].push(elementId)
			}

			//  Сохраняем состояние после удаления
			this.refreshModificationState()
			this.scheduleAutoSync('element-deleted')
		},
		handleMaterialVisibilityChanged(data) {
			const { type, id, isHidden } = data || {}
			if (!type || id === undefined || id === null) return
			if (!this.moduleElements[type] || !Array.isArray(this.moduleElements[type])) {
				return
			}

			const item = this.moduleElements[type].find(
				element => String(element.id) === String(id),
			)
			if (!item) return

			item.isHidden = isHidden
			item.is_hidden = isHidden
			if (item.fullData && typeof item.fullData === 'object') {
				item.fullData.isHidden = isHidden
				item.fullData.is_hidden = isHidden
			}

			this.refreshModificationState()
			this.scheduleAutoSync('material-visibility-changed')
		},
		getEditingStateStorageKey() {
			return `module_editing_state_${this.courseId}`
		},
		sanitizeForStorage(value, depth = 0) {
			const MAX_DEPTH = 12
			const MAX_STRING_LENGTH = 20000

			if (depth > MAX_DEPTH) {
				return null
			}
			if (value === null || value === undefined) {
				return value
			}
			if (value instanceof Date) {
				return value.toISOString()
			}
			if (typeof File !== 'undefined' && value instanceof File) {
				return null
			}
			if (typeof Blob !== 'undefined' && value instanceof Blob) {
				return null
			}
			if (typeof value === 'string') {
				if (value.startsWith('data:')) return ''
				if (value.startsWith('blob:')) return ''
				if (value.length > MAX_STRING_LENGTH) {
					return `${value.slice(0, MAX_STRING_LENGTH)}...[truncated]`
				}
				return value
			}
			if (Array.isArray(value)) {
				return value
					.map(item => this.sanitizeForStorage(item, depth + 1))
					.filter(item => item !== undefined)
			}
			if (typeof value === 'object') {
				const result = {}
				const skipKeys = new Set([
					'coverImage',
					'videoFile',
					'docFile',
					'imageFile',
					'file',
					'files',
				])

				Object.keys(value).forEach(key => {
					if (skipKeys.has(key)) return
					const lowerKey = key.toLowerCase()
					if (lowerKey.endsWith('file')) return
					if (lowerKey.includes('_file')) return

					const sanitized = this.sanitizeForStorage(value[key], depth + 1)
					if (sanitized !== undefined) {
						result[key] = sanitized
					}
				})

				return result
			}
			return value
		},
		buildEditingStateForStorage() {
			return {
				courseId: this.courseId,
				editingModule: this.sanitizeForStorage(this.editingModule),
				showCreationForm: this.showCreationForm,
				moduleTitle: this.moduleTitle,
				moduleDescription: this.moduleDescription,
				moduleDuration: this.moduleDuration,
				completionEnabled: this.completionEnabled,
				completionFinalTestId: this.completionFinalTestId,
				finalTestRequireLessons: this.finalTestRequireLessons,
				finalTestRequirePractice: this.finalTestRequirePractice,
				finalTestRequireHomework: this.finalTestRequireHomework,
				finalTestRequireTests: this.finalTestRequireTests,
				finalTestWindowEnabled: this.finalTestWindowEnabled,
				finalTestWindowDate: this.finalTestWindowDate,
				finalTestWindowTime: this.finalTestWindowTime,
				finalTestWindowDurationMinutes: this.finalTestWindowDurationMinutes,
				coverImage: null,
				coverImagePreview:
					typeof this.coverImagePreview === 'string' &&
					!this.coverImagePreview.startsWith('blob:')
						? this.coverImagePreview
						: null,
				publishType: this.publishType,
				publishDate: this.publishDate,
				publishTime: this.publishTime,
				moduleElements: this.sanitizeForStorage(this.moduleElements),
				deletedElements: this.sanitizeForStorage(this.deletedElements),
				initialModuleSnapshot: this.initialModuleSnapshot,
				timestamp: Date.now(),
			}
		},
		buildFallbackEditingStateForStorage() {
			const buildLightElements = list =>
				(Array.isArray(list) ? list : []).map(item => ({
					id: item?.id ?? null,
					title: item?.title || item?.fullData?.title || '',
					isDraft:
						item?.isDraft ??
						item?.is_draft ??
						item?.fullData?.isDraft ??
						item?.fullData?.is_draft ??
						false,
					isHidden:
						item?.isHidden ??
						item?.is_hidden ??
						item?.fullData?.isHidden ??
						item?.fullData?.is_hidden ??
						false,
					fullData: this.sanitizeForStorage({
						id: item?.fullData?.id ?? item?.id ?? null,
						title: item?.fullData?.title ?? item?.title ?? '',
						description: item?.fullData?.description || '',
						type: item?.fullData?.type || item?.fullData?.lesson_type || '',
						publishType: item?.fullData?.publishType || 'immediate',
						publishDate: item?.fullData?.publishDate || '',
						publishTime: item?.fullData?.publishTime || '',
						dueDate: item?.fullData?.dueDate || item?.due_date || null,
						dueTime: item?.fullData?.dueTime || item?.due_time || null,
						maxScore: item?.fullData?.maxScore ?? item?.max_score ?? null,
					}),
				}))

			return {
				courseId: this.courseId,
				editingModule: this.sanitizeForStorage(this.editingModule),
				showCreationForm: this.showCreationForm,
				moduleTitle: this.moduleTitle,
				moduleDescription: this.moduleDescription,
				moduleDuration: this.moduleDuration,
				completionEnabled: this.completionEnabled,
				completionFinalTestId: this.completionFinalTestId,
				finalTestRequireLessons: this.finalTestRequireLessons,
				finalTestRequirePractice: this.finalTestRequirePractice,
				finalTestRequireHomework: this.finalTestRequireHomework,
				finalTestRequireTests: this.finalTestRequireTests,
				finalTestWindowEnabled: this.finalTestWindowEnabled,
				finalTestWindowDate: this.finalTestWindowDate,
				finalTestWindowTime: this.finalTestWindowTime,
				finalTestWindowDurationMinutes: this.finalTestWindowDurationMinutes,
				coverImage: null,
				coverImagePreview: null,
				publishType: this.publishType,
				publishDate: this.publishDate,
				publishTime: this.publishTime,
				moduleElements: {
					lessons: buildLightElements(this.moduleElements?.lessons),
					tests: buildLightElements(this.moduleElements?.tests),
					homework: buildLightElements(this.moduleElements?.homework),
					practice: buildLightElements(this.moduleElements?.practice),
				},
				deletedElements: this.sanitizeForStorage(this.deletedElements),
				initialModuleSnapshot: null,
				timestamp: Date.now(),
				storageMode: 'fallback',
			}
		},

		//  Сохранение состояния редактирования в localStorage
		saveEditingStateToLocalStorage() {
			if (this.isRestoringModuleData) {
				return
			}
			// Проверяем, что courseId определен
			if (!this.courseId) {
				console.warn(
					'⚠️ courseId не определен, пропускаем сохранение состояния',
				)
				return
			}

			if (!this.editingModule && !this.showCreationForm) {
				// Если форма не открыта, очищаем localStorage
				localStorage.removeItem(this.getEditingStateStorageKey())
				return
			}

			if (!this.initialModuleSnapshot) {
				this.setInitialModuleSnapshot()
			}
			const storageKey = this.getEditingStateStorageKey()

			try {
				const state = this.buildEditingStateForStorage()
				localStorage.setItem(storageKey, JSON.stringify(state))
				this.storageQuotaWarningShown = false
				console.log('💾 Состояние редактирования сохранено в localStorage')
				return
			} catch (error) {
				if (error?.name !== 'QuotaExceededError') {
					console.error('❌ Ошибка сохранения состояния в localStorage:', error)
					return
				}
				console.warn('⚠️ Превышен лимит localStorage, сохраняем облегчённое состояние')
			}

			try {
				const fallbackState = this.buildFallbackEditingStateForStorage()
				localStorage.setItem(storageKey, JSON.stringify(fallbackState))
				if (!this.storageQuotaWarningShown) {
					console.warn(
						'⚠️ Состояние модуля сохранено в облегчённом режиме из-за лимита localStorage'
					)
					this.storageQuotaWarningShown = true
				}
			} catch (fallbackError) {
				console.error(
					'❌ Не удалось сохранить даже облегчённое состояние:',
					fallbackError
				)
				try {
					localStorage.removeItem(storageKey)
				} catch (cleanupError) {
					console.warn('⚠️ Не удалось очистить поврежденное состояние:', cleanupError)
				}
			}
		},

		//  Восстановление состояния редактирования из localStorage
		loadEditingStateFromLocalStorage() {
			// Проверяем, что courseId определен
			if (!this.courseId) {
				console.warn(
					'⚠️ courseId не определен, пропускаем восстановление состояния',
				)
				return
			}

			// В Bitrix режиме восстанавливаем только при явном возврате из preview
			const isBitrix = localStorage.getItem('bitrix24_mode') === 'true'
			const isPreviewRecovery = sessionStorage.getItem('preview_recovery_mode') === 'true'
			
			if (isBitrix && !isPreviewRecovery) {
				console.log('[ModuleDebug] skip restore in Bitrix without preview recovery')
				return
			}

			// Очищаем флаг после использования
			if (isPreviewRecovery) {
				sessionStorage.removeItem('preview_recovery_mode')
			}

			const stateKey = this.getEditingStateStorageKey()
			const savedState = localStorage.getItem(stateKey)

			if (!savedState) {
				console.log('ℹ️ Нет сохраненного состояния редактирования')
				return
			}

			try {
				const state = JSON.parse(savedState)

				// Проверяем, что состояние не старше 24 часов
				const maxAge = 24 * 60 * 60 * 1000 // 24 часа
				if (Date.now() - state.timestamp > maxAge) {
					console.log('⏰ Сохраненное состояние устарело, удаляем')
					localStorage.removeItem(stateKey)
					return
				}

				// Восстанавливаем состояние
				this.editingModule = state.editingModule
				this.showCreationForm = state.showCreationForm
				this.moduleTitle = state.moduleTitle
				this.moduleDescription = state.moduleDescription || ''
				this.moduleDuration = state.moduleDuration ?? null
				this.completionEnabled = !!state.completionEnabled
				this.completionFinalTestId = state.completionFinalTestId || ''
				this.finalTestRequireLessons = !!state.finalTestRequireLessons
				this.finalTestRequirePractice = !!state.finalTestRequirePractice
				this.finalTestRequireHomework = !!state.finalTestRequireHomework
				this.finalTestRequireTests = !!state.finalTestRequireTests
				this.finalTestWindowEnabled = !!state.finalTestWindowEnabled
				if (state.finalTestWindowDate || state.finalTestWindowTime) {
					this.finalTestWindowDate = state.finalTestWindowDate || ''
					this.finalTestWindowTime = state.finalTestWindowTime || ''
				} else if (state.finalTestWindowStartAt) {
					const parts = this.parseWindowStartAt(state.finalTestWindowStartAt)
					this.finalTestWindowDate = parts.date
					this.finalTestWindowTime = parts.time
				} else {
					this.finalTestWindowDate = ''
					this.finalTestWindowTime = ''
				}
				this.finalTestWindowDurationMinutes =
					state.finalTestWindowDurationMinutes ?? 120
				this.coverImage = state.coverImage
				this.coverImagePreview = state.coverImagePreview
				this.publishType = state.publishType
				this.publishDate = state.publishDate
				this.publishTime = state.publishTime
				this.moduleElements = state.moduleElements || {
					lessons: [],
					tests: [],
					homework: [],
					practice: [],
				}
				this.deletedElements = state.deletedElements || {
					lessons: [],
					tests: [],
					homework: [],
					practice: [],
				}
				this.initialModuleSnapshot = state.initialModuleSnapshot || null
				if (!this.initialModuleSnapshot) {
					this.setInitialModuleSnapshot()
				}

				console.log('✅ Состояние редактирования восстановлено из localStorage')

				// Прокручиваем к форме редактирования после полного рендеринга
				this.$nextTick(() => {
					// Добавляем задержку, чтобы форма успела полностью отрендериться
					setTimeout(() => {
						const formElement = document.getElementById('module-edit-form')
						if (formElement) {
							console.log('📜 Прокрутка к форме редактирования модуля')
							formElement.scrollIntoView({
								behavior: 'smooth',
								block: 'start',
							})
						} else {
							console.warn(
								'⚠️ Элемент module-edit-form не найден для прокрутки',
							)
						}
					}, 300) // 300ms задержка для гарантии рендеринга
				})
			} catch (error) {
				console.error('❌ Ошибка восстановления состояния:', error)
				localStorage.removeItem(stateKey)
			}
		},

		//  Очистка состояния редактирования из localStorage
		clearEditingStateFromLocalStorage() {
			localStorage.removeItem(this.getEditingStateStorageKey())
			this.storageQuotaWarningShown = false
			console.log('🗑️ Состояние редактирования удалено из localStorage')
		},
	},

	//  Восстановление состояния при монтировании компонента
	mounted() {
		//  Добавляем обработчик события beforeunload для блокировки закрытия вкладки
		window.addEventListener('beforeunload', this.handleBeforeUnload)
		window.addEventListener('auth:logout', this.closeEditingSession)
		window.addEventListener('focus', this.handlePreviewReturnFocus)

		// Запускаем проверку автопубликации каждые 30 секунд
		this.autoPublishCheckInterval = setInterval(() => {
			this.checkScheduledPublish()
		}, 30000)

		// Выполняем первую проверку сразу
		this.checkScheduledPublish()
	},

	// Очистка blob URLs и таймеров при уничтожении компонента
	beforeUnmount() {
		this.clearAutoSyncState()
		this.clearModulePrefetchTimer()
		this.isAutoSyncInProgress = false
		const previousPreview = this.coverImagePreview
		this.coverImagePreview = null
		this.safeRevokeBlobUrl(previousPreview)

		// Очистка debounce timeout для сохранения порядка модулей
		if (this._persistTimeout) {
			clearTimeout(this._persistTimeout)
			this._persistTimeout = null
		}

		// Очистка интервала проверки автопубликации
		if (this.autoPublishCheckInterval) {
			clearInterval(this.autoPublishCheckInterval)
			this.autoPublishCheckInterval = null
		}

		//  Удаляем обработчик события beforeunload
		window.removeEventListener('beforeunload', this.handleBeforeUnload)
		window.removeEventListener('auth:logout', this.closeEditingSession)
		window.removeEventListener('focus', this.handlePreviewReturnFocus)
	},
}
</script>

<style scoped>
/* ============================================
   MAIN CONTAINER
   ============================================ */
.mcf-container {
	min-height: 434px;
	position: relative;
	width: 100%;
	max-width: 1328px;
	margin: 0 auto;
	padding-bottom: 40px;
}

.mcf-module-loading {
	margin: 12px 0 16px;
	padding: 10px 14px;
	border-radius: 10px;
	background: #f2f8ff;
	border: 1px solid #d5e8ff;
	color: #2268a6;
	font-size: 14px;
	line-height: 1.4;
}

/* ============================================
   MODULES SECTION (расположена ВЫШЕ формы)
   ============================================ */
.mcf-modules-section {
	width: 100%;
	padding: 40px;
	margin-bottom: 40px;
	overflow: visible; /* Убираем скролл */
}

/* ============================================
   EMPTY STATE
   ============================================ */
.mcf-empty-state {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 60px 40px;
	background: linear-gradient(135deg, #f8fafc 0%, #eef2f7 100%);
	border-radius: 20px;
	border: 2px dashed #d1d9e6;
	text-align: center;
	animation: fadeInUp 0.5s ease;
}

@keyframes fadeInUp {
	from {
		opacity: 0;
		transform: translateY(20px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

.mcf-empty-state__illustration {
	margin-bottom: 24px;
}

.mcf-empty-state__icon {
	width: 120px;
	height: 120px;
	animation: float 3s ease-in-out infinite;
}

@keyframes float {
	0%,
	100% {
		transform: translateY(0);
	}
	50% {
		transform: translateY(-10px);
	}
}

.mcf-empty-state__content {
	max-width: 480px;
}

.mcf-empty-state__title {
	color: #1a1d21;
	font-family: 'Poppins', sans-serif;
	font-size: 24px;
	font-weight: 700;
	margin: 0 0 12px 0;
	line-height: 1.3;
}

.mcf-empty-state__text {
	color: #6b7280;
	font-family: 'Poppins', sans-serif;
	font-size: 15px;
	line-height: 1.6;
	margin: 0;
}

.mcf-modules-section--top {
	order: -1; /* Гарантирует, что секция будет выше формы */
}

.mcf-modules-header {
	align-items: center;
	display: flex;
	justify-content: space-between;
	margin-bottom: 40px;
}

.mcf-modules-title {
	color: #000000;
	font-size: 25px;
	font-weight: 700;
	letter-spacing: 0;
	line-height: normal;
	margin: 0;
}

.mcf-btn--create {
	background: #0096ed;
	border: none;
	border-radius: 8px;
	color: #ffffff;
	cursor: pointer;
	font-size: 17px;
	font-weight: 700;
	padding: 12px 24px;
	transition: all 0.3s ease;
}

.mcf-btn--create:hover {
	background-color: #0085d4;
	transform: translateY(-2px);
}

.mcf-modules-published,
.mcf-modules-drafts {
	margin-bottom: 50px;
	overflow: visible; /* Убираем скролл */
}

/* Убираем overflow в MaterialsSlider внутри модулей */
.mcf-modules-published :deep(.materials-slider),
.mcf-modules-drafts :deep(.materials-slider) {
	overflow: visible;
}

.mcf-modules-published :deep(.materials-slider.materials-slider--scroll),
.mcf-modules-drafts :deep(.materials-slider.materials-slider--scroll) {
	overflow: visible;
}

.mcf-modules-published :deep(.materials-slider__wrapper),
.mcf-modules-drafts :deep(.materials-slider__wrapper) {
	overflow: visible; /* Убираем overflow-x: hidden */
}

.mcf-modules-published :deep(.materials-slider__track),
.mcf-modules-published :deep(.materials-slider__page),
.mcf-modules-published :deep(.materials-slider__grid),
.mcf-modules-drafts :deep(.materials-slider__track),
.mcf-modules-drafts :deep(.materials-slider__page),
.mcf-modules-drafts :deep(.materials-slider__grid) {
	overflow: visible;
}

.mcf-modules-published :deep(.materials-slider__grid.materials-slider__grid--scroll),
.mcf-modules-drafts :deep(.materials-slider__grid.materials-slider__grid--scroll) {
	max-height: 82vh;
	overflow-y: auto;
	overflow-x: hidden;
	padding-right: 8px;
	padding-bottom: 28px;
	box-sizing: border-box;
}

.mcf-section-title {
	color: #000000;
	font-family: 'Poppins', Helvetica;
	font-size: 20px;
	font-weight: 600;
	letter-spacing: 0;
	line-height: normal;
	margin: 0 0 30px 0;
}

.mcf-drafts-header {
	align-items: center;
	display: flex;
	gap: 20px;
	margin-bottom: 30px;
}

.mcf-drafts-divider {
	background-color: rgba(0, 0, 0, 0.1);
	flex: 1;
	height: 1px;
}

/* ============================================
   FORM WRAPPER
   ============================================ */
.mcf-form-wrapper {
	width: 100%;
	padding: 40px;
}

/* ============================================
   PAGE TITLE
   ============================================ */
.mcf-title {
	color: #000000;
	font-family: 'Poppins', Helvetica;
	font-size: 25px;
	font-weight: 700;
	letter-spacing: 0;
	line-height: normal;
	margin: 0 0 20px 0;
	position: relative;
}

.mcf-title-hint {
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	line-height: 1.4;
	color: rgba(0, 0, 0, 0.7);
	margin: -12px 0 20px 0;
}

/* ============================================
   ACTION BUTTONS
   ============================================ */
.mcf-actions {
	display: flex;
	gap: 27px;
	height: 50px;
	position: relative;
	justify-content: flex-end;
	margin-bottom: 30px;
}

.mcf-btn {
	border-radius: 12px;
	cursor: pointer;
	font-family: 'Poppins', Helvetica;
	font-size: 20px;
	height: 50px;
	letter-spacing: 0;
	line-height: normal;
	transition: all 0.2s ease;
	position: relative;
	overflow: hidden;
}

.mcf-btn::before {
	content: '';
	position: absolute;
	top: 50%;
	left: 50%;
	width: 0;
	height: 0;
	border-radius: 50%;
	background: rgba(255, 255, 255, 0.2);
	transform: translate(-50%, -50%);
	transition: width 0.4s ease, height 0.4s ease;
}

.mcf-btn:active::before {
	width: 300px;
	height: 300px;
}

.mcf-btn--draft {
	background-color: #f5f5f5;
	border: none;
	color: rgba(0, 0, 0, 0.6);
	font-weight: 700;
	padding: 0 14px;
	width: 370px;
}

.mcf-btn--draft:hover {
	background-color: #e8e8e8;
	color: rgba(0, 0, 0, 0.8);
	transform: translateY(-2px);
	box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.mcf-btn--draft:active {
	transform: translateY(0);
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.mcf-btn--cancel {
	background-color: transparent;
	border: 1px solid rgba(243, 39, 39, 0.8);
	color: #f42828;
	font-weight: 400;
	padding: 0 20px;
	width: 240px;
}

.mcf-btn--cancel:hover {
	background-color: rgba(243, 39, 39, 0.05);
	border-color: #f42828;
	transform: translateY(-2px);
	box-shadow: 0 4px 8px rgba(243, 39, 39, 0.15);
}

.mcf-btn--cancel:active {
	transform: translateY(0);
	box-shadow: 0 2px 4px rgba(243, 39, 39, 0.1);
}

.mcf-btn--preview {
	background-color: rgba(0, 149, 236, 0.12);
	border: 1px solid rgba(0, 149, 236, 0.55);
	color: #0075ba;
	font-weight: 600;
	padding: 0 20px;
	min-width: 280px;
}

.mcf-btn--preview:hover {
	background-color: rgba(0, 149, 236, 0.2);
	border-color: #0095ec;
	transform: translateY(-2px);
	box-shadow: 0 4px 10px rgba(0, 149, 236, 0.2);
}

.mcf-btn--preview:active {
	transform: translateY(0);
	box-shadow: 0 2px 4px rgba(0, 149, 236, 0.12);
}

.mcf-btn--save {
	background-color: rgba(50, 205, 50, 0.8);
	border: none;
	color: #ffffff;
	font-weight: 700;
	padding: 0 25px;
	min-width: 220px;
	width: auto;
}

.mcf-btn--save:hover:not(:disabled) {
	background-color: #32cd32;
	transform: translateY(-2px);
	box-shadow: 0 4px 12px rgba(50, 205, 50, 0.3);
}

.mcf-btn--save:active:not(:disabled) {
	transform: translateY(0);
	box-shadow: 0 2px 6px rgba(50, 205, 50, 0.2);
}

.mcf-btn--save:disabled {
	background-color: #cccccc;
	color: #666666;
	cursor: not-allowed;
	opacity: 0.6;
}

/* ============================================
   VALIDATION WARNING
   ============================================ */
.mcf-validation-warning {
	background: linear-gradient(135deg, #fff3cd 0%, #ffe69c 100%);
	border: 2px solid #ffc107;
	border-radius: 12px;
	padding: 20px;
	margin: 20px 0;
	display: flex;
	gap: 15px;
	animation: slideDown 0.3s ease;
}

@keyframes slideDown {
	from {
		opacity: 0;
		transform: translateY(-10px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

.mcf-validation-warning__icon {
	font-size: 32px;
	flex-shrink: 0;
}

.mcf-validation-warning__content {
	flex: 1;
}

.mcf-validation-warning__title {
	color: #856404;
	font-family: 'Poppins', Helvetica;
	font-size: 18px;
	font-weight: 700;
	margin: 0 0 10px 0;
}

.mcf-validation-warning__text {
	color: #856404;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	margin: 0 0 10px 0;
}

.mcf-validation-warning__list {
	list-style: none;
	padding: 0;
	margin: 0 0 15px 0;
}

.mcf-validation-warning__item {
	color: #856404;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	padding: 5px 0;
	font-weight: 600;
}

.mcf-validation-warning__hint {
	color: #856404;
	font-family: 'Poppins', Helvetica;
	font-size: 13px;
	font-style: italic;
	margin: 0;
	padding: 10px;
	background: rgba(255, 255, 255, 0.5);
	border-radius: 6px;
}

/* ============================================
   MODULE COVER SECTION
   ============================================ */
.mcf-cover {
	height: 327px;
	position: relative;
	width: 100%;
	max-width: 523px;
	margin-bottom: 30px;
	left: 100px;
}

.mcf-cover__bg-overlay {
	aspect-ratio: 1.59;
	background-color: rgba(1, 1, 1, 0.2);
	border-radius: 20px;
	height: 327px;
	left: 0;
	position: absolute;
	top: 0;
	width: 520px;
	z-index: 2;
	transition: background-color 0.3s ease;
}

.mcf-cover:hover .mcf-cover__bg-overlay {
	background-color: rgba(1, 1, 1, 0.15);
}

.mcf-cover__bg {
	background-color: #bebebe;
	background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg"><rect fill="%23bebebe" width="100%" height="100%"/></svg>');
	background-position: center;
	background-size: cover;
	border-radius: 20px;
	height: 327px;
	left: 1px;
	position: absolute;
	top: 0;
	width: 521px;
	z-index: 1;
}

/* Upload Cover Button */
.mcf-upload-btn {
	background: transparent;
	border: none;
	cursor: pointer;
	height: 50px;
	left: 29px;
	padding: 0;
	position: absolute;
	top: 30px;
	width: 232px;
	z-index: 3;
	transition: transform 0.2s ease;
}

.mcf-upload-btn:hover {
	transform: scale(1.05);
}

.mcf-upload-btn:active {
	transform: scale(0.98);
}

.mcf-upload-btn__bg {
	backdrop-filter: blur(5px) brightness(100%);
	background-color: rgba(51, 51, 51, 0.4);
	border-radius: 10px;
	height: 50px;
	left: 0;
	position: absolute;
	top: 0;
	transition: all 0.3s ease;
	width: 230px;
}

.mcf-upload-btn:hover .mcf-upload-btn__bg {
	background-color: rgba(51, 51, 51, 0.6);
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.mcf-upload-btn__text {
	color: #ffffff;
	font-family: 'Poppins', Helvetica;
	font-size: 20px;
	font-weight: 700;
	left: 14px;
	letter-spacing: 0;
	line-height: normal;
	position: absolute;
	top: 10px;
	transition: color 0.3s ease;
}

.mcf-upload-btn:hover .mcf-upload-btn__text {
	color: #fff;
	text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

/* Remove Cover Button */
.mcf-remove-cover-btn {
	background: transparent;
	border: none;
	cursor: pointer;
	height: 40px;
	width: 40px;
	left: 270px;
	padding: 0;
	position: absolute;
	top: 35px;
	z-index: 3;
	transition: transform 0.2s ease;
}

.mcf-remove-cover-btn:hover {
	transform: scale(1.1);
}

.mcf-remove-cover-btn:active {
	transform: scale(0.95);
}

.mcf-remove-cover-btn__bg {
	backdrop-filter: blur(5px) brightness(100%);
	background-color: rgba(220, 53, 69, 0.5);
	border-radius: 50%;
	height: 40px;
	width: 40px;
	left: 0;
	position: absolute;
	top: 0;
	transition: all 0.3s ease;
}

.mcf-remove-cover-btn:hover .mcf-remove-cover-btn__bg {
	background-color: rgba(220, 53, 69, 0.8);
	box-shadow: 0 4px 12px rgba(220, 53, 69, 0.4);
}

.mcf-remove-cover-btn__icon {
	color: #ffffff;
	font-family: 'Poppins', Helvetica;
	font-size: 24px;
	font-weight: 700;
	position: absolute;
	left: 50%;
	top: 50%;
	transform: translate(-50%, -50%);
	line-height: 1;
	transition: color 0.3s ease;
}

.mcf-remove-cover-btn:hover .mcf-remove-cover-btn__icon {
	color: #fff;
	text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

/* Module Title Editor */
.mcf-title-editor {
	height: 79px;
	left: 1px;
	position: absolute;
	top: 248px;
	width: 524px;
	z-index: 3;
}

.mcf-title-editor__bg {
	backdrop-filter: blur(5px) brightness(100%);
	background-color: rgba(51, 51, 51, 0.4);
	border-radius: 0px 0px 20px 20px;
	height: 79px;
	left: 0;
	position: absolute;
	top: 0;
	width: 522px;
}

.mcf-title-editor__icon {
	aspect-ratio: 1;
	cursor: pointer;
	height: 15px;
	left: 35px;
	position: absolute;
	top: 32px;
	width: 15px;
	z-index: 2;
	transition: all 0.2s ease;
}

.mcf-title-editor__icon:hover {
	transform: scale(1.2) rotate(15deg);
	filter: brightness(1.3);
}

.mcf-title-editor__icon:active {
	transform: scale(1.1) rotate(0deg);
}

.mcf-title-editor__input {
	background: transparent;
	border: 2px solid transparent;
	border-radius: 8px;
	color: #ffffff;
	font-family: 'Calibri-Bold', Helvetica;
	font-size: 20px;
	font-weight: 700;
	left: 58px;
	letter-spacing: 0;
	line-height: normal;
	padding: 5px 10px;
	position: absolute;
	top: 20px;
	transition: all 0.3s ease;
	width: 383px;
	z-index: 1;
}

.mcf-title-editor__input::placeholder {
	color: rgba(255, 255, 255, 0.7);
}

.mcf-title-editor__input:hover {
	border-color: rgba(0, 149, 236, 0.4);
	background-color: rgba(0, 0, 0, 0.1);
}

.mcf-title-editor__input:focus {
	border-color: rgba(0, 149, 236, 0.8);
	outline: none;
	background-color: rgba(0, 0, 0, 0.2);
	box-shadow: 0 0 0 3px rgba(0, 149, 236, 0.2);
}

/* ============================================
   MODULE DETAILS SECTION (Description & Duration)
   ============================================ */
.mcf-details {
	margin: 24px 0;
	padding: 24px;
	background: linear-gradient(140deg, #ffffff 0%, #f7faff 100%);
	border-radius: 16px;
	border: 1px solid rgba(0, 149, 236, 0.14);
	box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
	position: relative;
	overflow: hidden;
}

.mcf-form-wrapper :deep(.mts-container) {
	top: -240px;
}

.mcf-details__grid {
	display: grid;
	grid-template-columns: 1fr;
	gap: 22px;
	position: relative;
	z-index: 1;
}

.mcf-completion {
	border: 1px solid rgba(0, 149, 236, 0.18);
	border-radius: 12px;
	padding: 20px;
	background: #ffffff;
	box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.03);
	display: flex;
	flex-direction: column;
	gap: 16px;
}

.mcf-completion > .mcf-checkbox {
	align-items: flex-start;
}

.mcf-completion__row {
	display: flex;
	flex-direction: column;
	gap: 10px;
}

.mcf-completion__row + .mcf-completion__row {
	padding-top: 14px;
	border-top: 1px dashed rgba(0, 149, 236, 0.16);
}

.mcf-completion__row--rules,
.mcf-completion__row--time {
	gap: 12px;
}

.mcf-completion__rules {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 8px 18px;
}

.mcf-completion__window {
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 12px 14px;
	align-items: start;
	margin-top: 8px;
}

.mcf-window-field {
	display: flex;
	flex-direction: column;
	gap: 6px;
}

.mcf-window-field--duration {
	margin-top: 0;
}

.mcf-checkbox--inline {
	align-items: center;
	gap: 8px;
	min-height: 30px;
}

.mcf-completion__hint {
	margin: 0;
	font-size: 12px;
	color: rgba(15, 23, 42, 0.62);
	font-family: 'Poppins', Helvetica;
	line-height: 1.45;
}

.mcf-completion__hint--warning {
	color: rgba(220, 53, 69, 0.84);
}

@media (max-width: 1024px) {
	.mcf-completion__window {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}
}

@media (max-width: 720px) {
	.mcf-completion {
		padding: 16px;
	}

	.mcf-completion__rules,
	.mcf-completion__window {
		grid-template-columns: 1fr;
	}

	.mcf-window-field--duration {
		margin-top: 0;
	}

	.mcf-completion__window {
		margin-top: 4px;
	}
}

.mcf-checkbox {
	display: flex;
	align-items: center;
	gap: 10px;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	color: #1a1a1a;
}

.mcf-checkbox__input {
	width: 18px;
	height: 18px;
	accent-color: #0095ec;
}

.mcf-checkbox__text {
	line-height: 1.4;
}

.mcf-field {
	position: relative;
}

.mcf-field--full {
	grid-column: 1 / -1;
}

.mcf-field__label {
	display: block;
	font-size: 12px;
	font-weight: 700;
	color: rgba(15, 23, 42, 0.75);
	text-transform: uppercase;
	letter-spacing: 0.08em;
	margin-bottom: 8px;
}

.mcf-field__label--compact {
	margin-bottom: 0;
	font-size: 10px;
	letter-spacing: 0.02em;
	text-transform: none;
}

.mcf-field__textarea {
	width: 100%;
	box-sizing: border-box;
	padding: 14px 16px;
	resize: vertical;
	background-color: #ffffff;
	border: 1px solid rgba(0, 149, 236, 0.2);
	border-radius: 12px;
	transition: border-color 0.2s ease, box-shadow 0.2s ease;
	color: #0f172a;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	line-height: 1.5;
	box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.04);
}

.mcf-field__textarea::placeholder {
	color: rgba(0, 0, 0, 0.4);
}

.mcf-field__textarea:focus {
	border-color: #0095ec;
	outline: none;
	box-shadow: 0 0 0 3px rgba(0, 149, 236, 0.12);
}

.mcf-field__input {
	width: 100%;
	box-sizing: border-box;
	padding: 12px 16px;
	background-color: #ffffff;
	border: 1px solid rgba(0, 149, 236, 0.2);
	border-radius: 12px;
	transition: border-color 0.2s ease, box-shadow 0.2s ease;
	color: #0f172a;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.04);
}

.mcf-field__input:focus {
	border-color: #0095ec;
	outline: none;
	box-shadow: 0 0 0 3px rgba(0, 149, 236, 0.12);
}

.mcf-field__input::placeholder {
	color: rgba(0, 0, 0, 0.4);
}

.mcf-field__hint {
	display: block;
	margin-top: 6px;
	font-size: 12px;
	color: rgba(0, 0, 0, 0.5);
	font-family: 'Poppins', Helvetica;
	font-style: italic;
}

.mcf-field__counter {
	margin-top: 6px;
	font-size: 12px;
	color: rgba(15, 23, 42, 0.6);
	text-align: right;
	font-family: 'Poppins', Helvetica;
}

.mcf-field__counter--limit {
	color: #dc2626;
	font-weight: 700;
}

/* ============================================
   AUTO-PUBLISH SETTINGS
   ============================================ */
.mcf-settings {
	background: linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%);
	border: 1px solid rgba(0, 149, 236, 0.15);
	border-radius: 16px;
	padding: 24px;
	position: relative;
	width: 100%;
	max-width: 450px;
	top: -360px;
	margin-left: 800px;
	box-shadow: 0 2px 12px rgba(0, 149, 236, 0.08);
	transition: all 0.3s ease;
}

.mcf-settings:hover {
	box-shadow: 0 4px 20px rgba(0, 149, 236, 0.15);
	transform: translateY(-2px);
}

/* Header с иконкой */
.mcf-settings__header {
	display: flex;
	align-items: center;
	gap: 12px;
	margin-bottom: 20px;
	padding-bottom: 16px;
	border-bottom: 2px solid rgba(0, 149, 236, 0.1);
}

.mcf-settings__icon {
	width: 40px;
	height: 40px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: linear-gradient(135deg, #0095ec 0%, #0077c2 100%);
	border-radius: 10px;
	color: #ffffff;
	box-shadow: 0 4px 12px rgba(0, 149, 236, 0.25);
}

.mcf-settings__title {
	color: #1a1a1a;
	font-family: 'Poppins', Helvetica;
	font-size: 18px;
	font-weight: 700;
	letter-spacing: -0.02em;
	line-height: 1.2;
	margin: 0;
}

/* Info Badge */
.mcf-settings__info {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 12px 16px;
	background: linear-gradient(135deg, #e3f2fd 0%, #f0f7ff 100%);
	border: 1px solid rgba(0, 149, 236, 0.2);
	border-radius: 10px;
	margin-top: 16px;
	color: #0077c2;
	font-family: 'Poppins', Helvetica;
	font-size: 13px;
	font-weight: 500;
	line-height: 1.4;
	animation: fadeInUp 0.3s ease;
}

.mcf-settings__info svg {
	flex-shrink: 0;
	color: #0095ec;
}

@keyframes fadeInUp {
	from {
		opacity: 0;
		transform: translateY(10px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

/* ============================================
   RADIO BUTTONS
   ============================================ */
.mcf-radio-group {
	display: flex;
	flex-direction: column;
	gap: 12px;
	margin-bottom: 20px;
}

.mcf-radio {
	align-items: flex-start;
	cursor: pointer;
	display: flex;
	gap: 12px;
	position: relative;
	padding: 16px;
	border-radius: 12px;
	border: 2px solid rgba(0, 0, 0, 0.08);
	background: #ffffff;
	transition: all 0.2s ease;
}

.mcf-radio:hover {
	border-color: rgba(0, 149, 236, 0.3);
	background: linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
	box-shadow: 0 2px 8px rgba(0, 149, 236, 0.1);
	transform: translateX(2px);
}

.mcf-radio__input:checked ~ .mcf-radio {
	border-color: #0095ec;
	background: linear-gradient(135deg, #e3f2fd 0%, #f0f7ff 100%);
	box-shadow: 0 4px 12px rgba(0, 149, 236, 0.15);
}

.mcf-radio__input {
	height: 0;
	opacity: 0;
	position: absolute;
	width: 0;
}

.mcf-radio__custom {
	width: 20px;
	height: 20px;
	background-color: #ffffff;
	border: 2px solid rgba(0, 0, 0, 0.3);
	border-radius: 50%;
	flex-shrink: 0;
	position: relative;
	transition: all 0.2s ease;
	margin-top: 2px;
}

.mcf-radio:hover .mcf-radio__custom {
	border-color: #0095ec;
	transform: scale(1.1);
	box-shadow: 0 0 0 4px rgba(0, 149, 236, 0.1);
}

.mcf-radio__input:checked + .mcf-radio__custom {
	border-color: #0095ec;
	background: #0095ec;
	box-shadow: 0 0 0 4px rgba(0, 149, 236, 0.15);
}

.mcf-radio__input:checked + .mcf-radio__custom::before {
	content: '';
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	width: 8px;
	height: 8px;
	background-color: #ffffff;
	border-radius: 50%;
	animation: radioCheck 0.3s ease;
}

@keyframes radioCheck {
	0% {
		transform: translate(-50%, -50%) scale(0);
	}
	50% {
		transform: translate(-50%, -50%) scale(1.3);
	}
	100% {
		transform: translate(-50%, -50%) scale(1);
	}
}

.mcf-radio__content {
	display: flex;
	flex-direction: column;
	gap: 4px;
	flex: 1;
}

.mcf-radio__title-wrapper {
	display: flex;
	align-items: center;
	gap: 8px;
}

.mcf-radio__emoji {
	font-size: 18px;
	line-height: 1;
}

.mcf-radio__title {
	color: #1a1a1a;
	font-family: 'Poppins', Helvetica;
	font-size: 16px;
	font-weight: 600;
	letter-spacing: -0.01em;
	line-height: 1.3;
	transition: color 0.2s ease;
}

.mcf-radio:hover .mcf-radio__title {
	color: #0095ec;
}

.mcf-radio__input:checked ~ .mcf-radio__content .mcf-radio__title {
	color: #0095ec;
}

.mcf-radio__desc {
	color: rgba(26, 26, 26, 0.6);
	font-family: 'Poppins', Helvetica;
	font-size: 13px;
	font-weight: 400;
	letter-spacing: 0;
	line-height: 1.5;
}

/* ============================================
   DATE & TIME INPUTS
   ============================================ */
.mcf-datetime-group {
	display: grid;
	grid-template-columns: minmax(0, 1fr) 170px;
	gap: 16px;
	margin-bottom: 16px;
}

.mcf-field {
	position: relative;
}

.mcf-field--date,
.mcf-field--time {
	position: relative;
}

.mcf-field--time {
	max-width: 210px;
}

.mcf-field__label {
	display: flex;
	align-items: center;
	gap: 6px;
	color: rgba(26, 26, 26, 0.8);
	font-family: 'Poppins', Helvetica;
	font-size: 13px;
	font-weight: 600;
	letter-spacing: 0;
	line-height: 1.3;
	margin-bottom: 8px;
}

.mcf-field__label svg {
	color: #0095ec;
	flex-shrink: 0;
}

.mcf-field__input {
	width: 100%;
	padding: 12px 14px;
	background-color: #ffffff;
	border: 2px solid rgba(0, 0, 0, 0.1);
	border-radius: 10px;
	color: #1a1a1a;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	font-weight: 500;
	transition: all 0.2s ease;
	cursor: pointer;
}

.mcf-field__input:hover:not(:disabled) {
	border-color: rgba(0, 149, 236, 0.4);
	background-color: #fafbff;
}

.mcf-field__input:focus {
	outline: none;
	border-color: #0095ec;
	background-color: #ffffff;
	box-shadow: 0 0 0 4px rgba(0, 149, 236, 0.1);
}

.mcf-field__input:disabled {
	background-color: #f5f5f5;
	border-color: rgba(0, 0, 0, 0.05);
	color: rgba(26, 26, 26, 0.3);
	cursor: not-allowed;
	opacity: 0.6;
}

/* Стили для календаря и времени */
.mcf-field__input[type='date']::-webkit-calendar-picker-indicator,
.mcf-field__input[type='time']::-webkit-calendar-picker-indicator {
	cursor: pointer;
	filter: opacity(0.6);
	transition: filter 0.2s ease;
}

.mcf-field__input[type='date']:hover::-webkit-calendar-picker-indicator,
.mcf-field__input[type='time']:hover::-webkit-calendar-picker-indicator {
	filter: opacity(1);
}

.mcf-field__input[type='date']:disabled::-webkit-calendar-picker-indicator,
.mcf-field__input[type='time']:disabled::-webkit-calendar-picker-indicator {
	filter: opacity(0.3);
	cursor: not-allowed;
}

/* ============================================
   DRAG AND DROP STYLES
   ============================================ */

/* Ghost элемент - следует за курсором */
.mcf-drag-ghost {
	position: fixed;
	z-index: 10000;
	pointer-events: none;
	width: 300px;
	transform: rotate(3deg) scale(1.05);
	transition: none;
	filter: drop-shadow(0 25px 50px rgba(0, 0, 0, 0.3));
}

.mcf-drag-ghost__card {
	background: #ffffff;
	border-radius: 16px;
	overflow: hidden;
	box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3), 0 10px 20px rgba(0, 0, 0, 0.2),
		0 0 0 3px rgba(0, 149, 236, 0.4);
	animation: ghostAppear 0.2s ease-out;
}

@keyframes ghostAppear {
	from {
		opacity: 0;
		transform: scale(0.8);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
}

.mcf-drag-ghost__cover {
	height: 120px;
	background-size: cover;
	background-position: center;
}

.mcf-drag-ghost__content {
	padding: 16px;
}

.mcf-drag-ghost__title {
	font-family: 'Poppins', Helvetica;
	font-size: 16px;
	font-weight: 600;
	color: #1a1a1a;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

/* Обёртка карточки для drag */
.mcf-draggable-card {
	position: relative;
	cursor: grab;
	transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), filter 0.3s ease;
	border-radius: 16px;
	will-change: transform;
}

/* Hover - лёгкое поднятие без тени */
.mcf-draggable-card:hover:not(.mcf-draggable-card--dragging):not(
		.mcf-draggable-card--placeholder
	) {
	transform: translateY(-4px);
	filter: brightness(1.02);
}

/* Active - нажатие перед drag */
.mcf-draggable-card:active:not(.mcf-draggable-card--placeholder) {
	cursor: grabbing;
	transform: scale(0.98);
	transition: transform 0.1s ease;
}

/* Placeholder - место откуда взяли карточку */
.mcf-draggable-card--placeholder {
	opacity: 0.3;
	transform: scale(0.95);
	box-shadow: none;
	pointer-events: none;
}

.mcf-draggable-card--placeholder::after {
	content: '';
	position: absolute;
	inset: 0;
	border: 3px dashed rgba(0, 149, 236, 0.4);
	border-radius: 16px;
	background: rgba(0, 149, 236, 0.05);
}

/* Зона drop - подсветка целевой карточки */
.mcf-draggable-card--drag-over {
	transform: translateY(-8px);
	outline: 3px solid rgba(0, 149, 236, 0.6);
	outline-offset: 2px;
	transition: all 0.2s ease;
}

.mcf-draggable-card--drag-over::before {
	content: 'Отпустите здесь';
	position: absolute;
	top: -30px;
	left: 50%;
	transform: translateX(-50%);
	padding: 6px 12px;
	background: #0095ec;
	color: white;
	font-family: 'Poppins', Helvetica;
	font-size: 12px;
	font-weight: 600;
	border-radius: 6px;
	white-space: nowrap;
	z-index: 10;
	animation: dropHintPulse 1s ease-in-out infinite;
}

@keyframes dropHintPulse {
	0%,
	100% {
		opacity: 0.9;
		transform: translateX(-50%) translateY(0);
	}
	50% {
		opacity: 1;
		transform: translateX(-50%) translateY(-3px);
	}
}

/* Drag Handle - точки для захвата */
.mcf-drag-handle {
	position: absolute;
	top: 12px;
	left: 12px;
	width: 28px;
	height: 28px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: rgba(255, 255, 255, 0.95);
	backdrop-filter: blur(8px);
	border: 1px solid rgba(0, 0, 0, 0.08);
	border-radius: 6px;
	z-index: 15;
	opacity: 0;
	transform: scale(0.8);
	transition: all 0.2s ease;
	cursor: grab;
}

.mcf-draggable-card:hover .mcf-drag-handle {
	opacity: 1;
	transform: scale(1);
}

.mcf-drag-handle:hover {
	background: #ffffff;
	border-color: rgba(0, 149, 236, 0.3);
	box-shadow: 0 2px 8px rgba(0, 149, 236, 0.15);
}

.mcf-drag-handle:active {
	cursor: grabbing;
	transform: scale(0.95);
	background: rgba(0, 149, 236, 0.1);
}

/* Точки в drag handle */
.mcf-drag-handle__dots {
	display: grid;
	grid-template-columns: repeat(2, 4px);
	grid-template-rows: repeat(3, 4px);
	gap: 3px;
}

.mcf-drag-handle__dots::before,
.mcf-drag-handle__dots::after {
	content: '';
	width: 4px;
	height: 4px;
	background: rgba(0, 0, 0, 0.35);
	border-radius: 50%;
	box-shadow: 7px 0 0 rgba(0, 0, 0, 0.35), 0 7px 0 rgba(0, 0, 0, 0.35),
		7px 7px 0 rgba(0, 0, 0, 0.35), 0 14px 0 rgba(0, 0, 0, 0.35),
		7px 14px 0 rgba(0, 0, 0, 0.35);
}

.mcf-drag-handle__dots::after {
	display: none;
}

.mcf-drag-handle:hover .mcf-drag-handle__dots::before {
	background: rgba(0, 149, 236, 0.6);
	box-shadow: 7px 0 0 rgba(0, 149, 236, 0.6), 0 7px 0 rgba(0, 149, 236, 0.6),
		7px 7px 0 rgba(0, 149, 236, 0.6), 0 14px 0 rgba(0, 149, 236, 0.6),
		7px 14px 0 rgba(0, 149, 236, 0.6);
}

/* Анимация индикатора drop */
@keyframes dropIndicatorPulse {
	0%,
	100% {
		opacity: 0.7;
		transform: scaleX(0.95);
	}
	50% {
		opacity: 1;
		transform: scaleX(1);
	}
}

/* Плавная анимация перестановки */
.mcf-modules-published .mcf-draggable-card {
	transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), filter 0.3s ease,
		opacity 0.2s ease;
}

/* ============================================
   ACCESSIBILITY
   ============================================ */
.mcf-radio__input:focus + .mcf-radio__custom {
	box-shadow: 0 0 0 3px rgba(0, 149, 236, 0.3);
	outline: 2px solid #0095ec;
	outline-offset: 2px;
}

.mcf-btn:focus,
.mcf-upload-btn:focus,
.mcf-remove-cover-btn:focus,
.mcf-settings__save-btn:focus,
.mcf-field__input:focus,
.mcf-title-editor__input:focus {
	outline: 2px solid #0095ec;
	outline-offset: 2px;
}
</style>
